/* Personalised Football Player Print - built by scripts/theme/build.ts; do not edit here. */
(function () {
"use strict";

const DATA = window.PP_DATA;

/* ---------- colour, matching scripts/artgen/keys.ts ---------- */

const KEY_NAMES = ["kit", "splash", "skin", "hair"];
const MIN_SATURATION = 0.16, MAX_LIGHTNESS = 0.93;
/* Every pixel goes to whichever key is nearest, so two keys facing each other
   meet in the middle and nothing is left between them; MAX_REACH is only the
   outer limit, past which a pixel belongs to nobody. Within CONTESTED degrees
   of the line between two keys both of them show, because a faded undercut is
   painted as a gradient from the hair colour into the skin colour and the
   honest answer halfway along is half of each. */
const MAX_REACH = 40, CONTESTED = 20;
/* A faded undercut is a wash too weak to key on its own; trusted only where it
   touches confidently keyed hair, because a grey sock shadow shares its hue. */
const HAIR_WASH_SATURATION = 0.05, HAIR_WASH_LIGHTNESS = 0.95, HAIR_FULL_SATURATION = 0.09;
/* How much of a highlight on the hair survives repainting: the model puts a
   shine on the crown, and repainted brown that reads as thinning. */
const HAIR_HIGHLIGHT = 0.3;
/* How far the hair region reaches beyond keyed hair, as a share of the figure's width. */
const HAIR_MASK_REACH = 0.035;
/* Where a blue wash thins over the cyan skin the mixture lands in the gap
   between the two keys; inside the mask the reach is deliberately wide. */
const HAIR_WASH_REACH = 45;

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b), l = (max+min)/2;
  if (max === min) return [0, 0, l];
  const d = max-min, s = l > 0.5 ? d/(2-max-min) : d/(max+min);
  let h;
  if (max === r) h = ((g-b)/d + (g<b?6:0))/6; else if (max === g) h = ((b-r)/d + 2)/6; else h = ((r-g)/d + 4)/6;
  return [h*360, s, l];
}
function hslToRgb(h, s, l) {
  const f = (n) => { const k = (n + h/30) % 12, a = s * Math.min(l, 1-l);
    return Math.round(Math.max(0, Math.min(1, l - a*Math.max(-1, Math.min(k-3, Math.min(9-k, 1))))) * 255); };
  return [f(0), f(8), f(4)];
}
const hexToHsl = (hex) => rgbToHsl(parseInt(hex.slice(1,3),16), parseInt(hex.slice(3,5),16), parseInt(hex.slice(5,7),16));
const hueDistance = (a, b) => { const d = Math.abs(a-b) % 360; return d > 180 ? 360-d : d; };
const luminance = (hex) => (parseInt(hex.slice(1,3),16)*0.299 + parseInt(hex.slice(3,5),16)*0.587 + parseInt(hex.slice(5,7),16)*0.114) / 255;

/* Each painting carries its own measured keys: watercolour drifts between
   generations, so fixed hues would read one layer as another. */
function classifyWith(keys, r, g, b) {
  const [h, s, l] = rgbToHsl(r, g, b);
  if (s < MIN_SATURATION || l > MAX_LIGHTNESS) return null;
  let best = null, bestDistance = Infinity, second = null, secondDistance = Infinity;
  for (const key of KEY_NAMES) {
    const distance = hueDistance(h, keys[key].hue);
    if (distance < bestDistance) { second = best; secondDistance = bestDistance; best = key; bestDistance = distance; }
    else if (distance < secondDistance) { second = key; secondDistance = distance; }
  }
  if (best === null || bestDistance > MAX_REACH) return null;
  const mix = Math.max(0, Math.min(0.5, 0.5 - (secondDistance - bestDistance) / (2 * CONTESTED)));
  return { key: best, second: mix > 0 ? second : null, h, s, l, distance: bestDistance, mix };
}

/* Grows a mask by `radius` pixels, one axis at a time. A pixel is set when any pixel within `radius` of it along the
   axis is set, read from a running count, so the cost no longer grows with the radius: the same answer as checking
   each neighbour (which took about two seconds a painting on a phone), in a fraction of the time. */
function dilate(mask, width, height, radius) {
  const pass = new Uint8Array(width * height), count = new Int32Array(Math.max(width, height) + 1);
  for (let y = 0; y < height; y++) {
    const row = y * width;
    for (let x = 0; x < width; x++) count[x + 1] = count[x] + (mask[row + x] ? 1 : 0);
    for (let x = 0; x < width; x++) pass[row + x] = count[Math.min(width, x + radius + 1)] - count[Math.max(0, x - radius)] > 0 ? 1 : 0;
  }
  const out = new Uint8Array(width * height);
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) count[y + 1] = count[y] + (pass[y * width + x] ? 1 : 0);
    for (let y = 0; y < height; y++) out[y * width + x] = count[Math.min(height, y + radius + 1)] - count[Math.max(0, y - radius)] > 0 ? 1 : 0;
  }
  return out;
}

/* Where a faint wash of the hair key may be trusted as hair. */
function hairWashMask(data, width, height, keys, radius, classify) {
  const core = new Uint8Array(width * height);
  for (let p = 0; p < width * height; p++) {
    const i = p * 4;
    if (data[i + 3] === 0) continue;
    const c = classify ? classify(data[i], data[i + 1], data[i + 2]) : classifyWith(keys, data[i], data[i + 1], data[i + 2]);
    // the mixed lower half of a fade counts as hair here, so the mask reaches
    // the faint wash beneath it rather than stopping where the hair stops
    if (c && (c.key === "hair" || c.second === "hair")) core[p] = 1;
  }
  return dilate(core, width, height, radius);
}

/* How completely a pixel is repainted: only how strongly it was painted in the
   first place, because a thin wash cannot be covered by a colour laid over it.
   Which layer it belongs to no longer comes into it - a pixel caught between
   two keys is painted as a mixture of both. */
function repaintStrength(c, saturationFloor) {
  return Math.min(1, c.s / saturationFloor);
}
/* Whether a hue near the hair is the faded part of a haircut rather than skin:
   anything on the hair's side of the skin key, past a small margin, is stubble. */
const FADE_MARGIN = 10;
function fadeIsHair(hue, keys) {
  const towardsHair = Math.sign((((keys.hair.hue - keys.skin.hue) % 360) + 540) % 360 - 180);
  const offset = (((hue - keys.skin.hue) % 360) + 540) % 360 - 180;
  return Math.sign(offset) === towardsHair && Math.abs(offset) > FADE_MARGIN;
}
/* Which of hair and skin a faint wash near the hair belongs to, by hue. */
function washOwner(hue, keys) {
  const toHair = hueDistance(hue, keys.hair.hue), toSkin = hueDistance(hue, keys.skin.hue);
  if (Math.min(toHair, toSkin) > HAIR_WASH_REACH) return null;
  return toHair <= toSkin ? "hair" : "skin";
}
/* Mixes two hues the short way round the circle. */
function mixHue(a, b, t) {
  const d = ((b - a + 540) % 360) - 180;
  return ((a + d * t) % 360 + 360) % 360;
}

/* ---------- repainting, matching scripts/artgen/recolour.ts ---------- */

/*
 * Which layer each pixel belongs to, and how completely to repaint it.
 *
 * None of this depends on the colours the customer picks - only on the painting
 * - so it is worked out once per painting and kept. Doing it again on every
 * click is what made changing a hair colour take seconds: the answer was
 * already known, and thrown away.
 */
const KEY_NONE = 0, KEY_KIT = 1, KEY_SPLASH = 2, KEY_SKIN = 3, KEY_HAIR = 4;
const KEY_INDEX = { kit: KEY_KIT, splash: KEY_SPLASH, skin: KEY_SKIN, hair: KEY_HAIR };
/** Set as well as the layer where the pixel is faint splash, to be wiped with it. */
const SPLASH_WASH = 8;

const plans = new Map();
function figurePlan(code, source, keys, maskRadius) {
  const cached = plans.get(code);
  if (cached) return cached;

  const d = source.data, width = source.width, height = source.height, n = width * height;
  const key = new Uint8Array(n), second = new Uint8Array(n), mix = new Uint8Array(n);
  const strength = new Uint8Array(n), lum = new Uint8Array(n), sat = new Uint8Array(n);
  // a painting repeats its colours many times over: each colour is classified once and the answer reused (the
  // same answer as working it out again, a good deal faster, so a newly chosen haircut appears without a wait)
  const classified = new Map(), hsl = new Map();
  const classify = (r, g, b) => { const k = (r << 16) | (g << 8) | b; let v = classified.get(k); if (v === undefined) { v = classifyWith(keys, r, g, b) || null; classified.set(k, v); } return v; };
  const hslOf = (r, g, b) => { const k = (r << 16) | (g << 8) | b; let v = hsl.get(k); if (v === undefined) { v = rgbToHsl(r, g, b); hsl.set(k, v); } return v; };
  const hairNear = hairWashMask(d, width, height, keys, maskRadius, classify);
  const hairAlpha = new Uint8Array(n);

  for (let p = 0; p < n; p++) {
    const i = p * 4;
    const a = d[i + 3];
    if (a === 0) continue;
    const [h, sv, lv] = hslOf(d[i], d[i + 1], d[i + 2]);
    lum[p] = Math.round(lv * 255);
    sat[p] = Math.round(Math.min(1, sv) * 255);
    if (sv > 0.035 && hueDistance(h, keys.splash.hue) <= keys.splash.tolerance) key[p] |= SPLASH_WASH;

    let c = classify(d[i], d[i + 1], d[i + 2]);
    // A fringe, a faded undercut or the shadow inside an ear is a wash too weak
    // in colour to key at all. Near the hair it goes to whichever of hair and
    // skin is nearer, and is painted completely, or its own colour shows through.
    const wash = !c && hairNear[p] && sv >= HAIR_WASH_SATURATION && lv <= HAIR_WASH_LIGHTNESS
      ? washOwner(h, keys) : null;
    if (wash) c = { key: wash, second: null, h, s: sv, l: lv, distance: 0, mix: 0 };
    // the faded part of a haircut is nearer skin by hue but is hair, cut short
    // and it is hair all the way down: a mixture with skin partway through it
    // showed as a paler band between the hair above and the stubble below
    if (c && hairNear[p] && (c.key === "skin" || c.key === "hair") && fadeIsHair(h, keys)) c = { key: "hair", second: null, h, s: sv, l: lv, distance: 0, mix: 0 };
    if (!c) continue;
    key[p] |= KEY_INDEX[c.key];
    if (c.second) { second[p] = KEY_INDEX[c.second]; mix[p] = Math.round(c.mix * 255); }
    const floor = c.key === "hair" || c.second === "hair" ? HAIR_FULL_SATURATION : 0.22;
    const st = wash ? 1 : repaintStrength(c, floor);
    strength[p] = Math.round(st * 255);
    const share = c.key === "hair" ? 1 - c.mix : c.second === "hair" ? c.mix : 0;
    if (share > 0) hairAlpha[p] = Math.round(255 * share * st * (a / 255));
  }

  // grown by two pixels so the ink outlining the hair comes with it, rather
  // than leaving a hairline of letter showing along the edge of a ponytail
  const plan = { key, second, mix, strength, lum, sat, width, height,
                 hairAlpha: growAlpha(hairAlpha, width, height, 2) };
  plans.set(code, plan);
  return plan;
}

/*
 * Every colour a target can come out as, worked out up front.
 *
 * A repainted pixel keeps its own lightness and some of its own saturation, so
 * the answer depends on two numbers and nothing else. There are only a few
 * thousand combinations of those, against three quarters of a million pixels.
 */
const SAT_STEPS = 16;
function paintLut(target, pivot, highlight) {
  const lut = new Uint8Array(SAT_STEPS * 256 * 3);
  for (let sb = 0; sb < SAT_STEPS; sb++) {
    const s = (sb + 0.5) / SAT_STEPS;
    const saturation = Math.min(1, target[1] * (0.55 + s * 0.75));
    for (let lb = 0; lb < 256; lb++) {
      let lift = lb / 255 - pivot;
      if (highlight !== undefined && lift > 0) lift *= highlight;
      const shifted = Math.max(0.04, Math.min(0.97, target[2] + lift));
      const [r, g, b] = hslToRgb(target[0], saturation, shifted);
      const at = (sb * 256 + lb) * 3;
      lut[at] = r; lut[at + 1] = g; lut[at + 2] = b;
    }
  }
  return lut;
}

function repaint(source, out, plan, pivots, opts, geo) {
  const src = source.data, dst = out.data, w = out.width;
  const { key, second, mix, strength, lum, sat } = plan;
  const pattern = opts.kit.pattern || "solid";
  const shirt = geo.shirt, hemY = geo.hemY, hemCurve = geo.hemCurve, sleevesY = geo.sleevesY, socksY = geo.socksY;
  const bandW = (shirt.right - shirt.left) / 6, bandH = (shirt.bottom - shirt.top) / 5, pinW = (shirt.right - shirt.left) / 11;
  const clearSplash = opts.splash.mode === "none";
  const rainbow = opts.splash.mode === "rainbow";

  // a kit in four parts: shirt, sleeves, shorts, socks
  const kitPrimary = paintLut(hexToHsl(opts.kit.primary), pivots.kit);
  const kitSecondary = opts.kit.secondary && pattern !== "solid"
    ? paintLut(hexToHsl(opts.kit.secondary), pivots.kit) : kitPrimary;
  const kitSleeves = opts.kit.sleeves ? paintLut(hexToHsl(opts.kit.sleeves), pivots.kit) : null;
  const kitShorts = opts.kit.shorts ? paintLut(hexToHsl(opts.kit.shorts), pivots.kit) : kitPrimary;
  const kitSocks = opts.kit.socks ? paintLut(hexToHsl(opts.kit.socks), pivots.kit) : kitPrimary;
  // the fade is the only pattern that is not one table or the other
  const fadeLuts = pattern === "fade" && opts.kit.secondary
    ? Array.from({ length: 8 }, (_, i) => { const t = i / 7, a = hexToHsl(opts.kit.primary), b = hexToHsl(opts.kit.secondary);
        return paintLut([a[0], a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t], pivots.kit); })
    : null;
  const hairLut = paintLut(hexToHsl(opts.hair), pivots.hair, HAIR_HIGHLIGHT);
  const skinLut = paintLut(hexToHsl(opts.skin), pivots.skin);
  const splashLut = opts.splash.mode === "colour"
    ? paintLut(hexToHsl(opts.splash.colour), pivots.splash) : null;

  /* Which table paints a given layer at this pixel, and where to read it. */
  function lutFor(k, sx, sy) {
    if (k === KEY_KIT) {
      if (sy >= socksY) return kitSocks;
      // the hem is not straight: lowest in the middle, higher at the sides
      if (sy >= (hemCurve ? hemCurve[Math.max(0, Math.min(hemCurve.length - 1, Math.round(sx)))] : hemY)) return kitShorts;
      if (kitSleeves && sy < sleevesY && (sx < shirt.left || sx > shirt.right)) return kitSleeves;
      if (pattern === "solid") return kitPrimary;
      if (fadeLuts) return fadeLuts[Math.round(Math.max(0, Math.min(1, (sy - shirt.top) / (hemY - shirt.top))) * 7)];
      if (pattern === "pinstripes") return ((((sx - shirt.left) % pinW) + pinW) % pinW) < pinW * 0.22 ? kitSecondary : kitPrimary;
      const band = pattern === "stripes" ? Math.floor((sx - shirt.left) / bandW)
                 : pattern === "hoops"   ? Math.floor((sy - shirt.top) / bandH)
                 : sx < shirt.centreX ? 0 : 1;
      return ((band % 2) + 2) % 2 === 1 ? kitSecondary : kitPrimary;
    }
    if (k === KEY_HAIR) return hairLut;
    if (k === KEY_SKIN) return skinLut;
    return splashLut;
  }

  const n = w * out.height;
  for (let p = 0; p < n; p++) {
    const i = p * 4;
    const a = src[i + 3];
    if (a === 0 || (clearSplash && (key[p] & SPLASH_WASH))) {
      dst[i] = 0; dst[i + 1] = 0; dst[i + 2] = 0; dst[i + 3] = 0;
      continue;
    }
    let r = src[i], g = src[i + 1], b = src[i + 2];
    const k = key[p] & 7;
    if (k !== KEY_NONE) {
      const px = p % w, py = (p / w) | 0;
      const sx = px / geo.scale + geo.offsetX, sy = py / geo.scale + geo.offsetY;
      const at = (((sat[p] * SAT_STEPS) >> 8) * 256 + lum[p]) * 3;
      let tr = -1, tg = 0, tb = 0;
      if (k === KEY_SPLASH && rainbow) {
        // a hue per column: too few pixels to be worth a table each
        const s = sat[p] / 255;
        const shifted = Math.max(0.04, Math.min(0.97, 0.5 + (lum[p] / 255 - pivots.splash)));
        const mixed = hslToRgb(((sx / DATA.width) * 300 + 340) % 360, Math.min(1, 0.85 * (0.55 + s * 0.75)), shifted);
        tr = mixed[0]; tg = mixed[1]; tb = mixed[2];
      } else {
        const lut = lutFor(k, sx, sy);
        if (lut) { tr = lut[at]; tg = lut[at + 1]; tb = lut[at + 2]; }
      }
      // a pixel caught between two keys is painted as a mixture of both
      const k2 = second[p];
      if (tr >= 0 && k2 !== KEY_NONE && !(k2 === KEY_SPLASH && (rainbow || !splashLut))) {
        const lut2 = lutFor(k2, sx, sy);
        if (lut2) {
          const m = mix[p] / 255, keep = 1 - m;
          tr = tr * keep + lut2[at] * m;
          tg = tg * keep + lut2[at + 1] * m;
          tb = tb * keep + lut2[at + 2] * m;
        }
      }
      if (tr >= 0) {
        const st = strength[p] / 255, keep = 1 - st;
        r = (r * keep + tr * st) | 0;
        g = (g * keep + tg * st) | 0;
        b = (b * keep + tb * st) | 0;
      }
    }
    dst[i] = r; dst[i + 1] = g; dst[i + 2] = b; dst[i + 3] = a;
  }
}

/* Grows a soft mask outwards, keeping the strongest value found. */
function growAlpha(mask, width, height, radius) {
  const pass = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    const row = y * width;
    for (let x = 0; x < width; x++) {
      let best = 0;
      for (let d = -radius; d <= radius; d++) { const nx = x + d; if (nx >= 0 && nx < width && mask[row + nx] > best) best = mask[row + nx]; }
      pass[row + x] = best;
    }
  }
  const out = new Uint8Array(width * height);
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      let best = 0;
      for (let d = -radius; d <= radius; d++) { const ny = y + d; if (ny >= 0 && ny < height && pass[ny * width + x] > best) best = pass[ny * width + x]; }
      out[y * width + x] = best;
    }
  }
  return out;
}

/* ---------- the shirt lettering, matching src/render/layoutShirtText.ts ---------- */

const ARC_FILL_LIMIT = 0.94;
const advance = (ch, m) => m.advances[ch] === undefined ? m.fallbackAdvance : m.advances[ch];
function measureText(text, m, size, tracking) {
  if (!text.length) return 0;
  let units = 0; for (const ch of text) units += advance(ch, m);
  return (units / m.unitsPerEm) * size + tracking * (text.length - 1);
}
const r2 = (n) => Math.round(n * 100) / 100;
const halfAngle = (g) => Math.asin(Math.min(1, g.chordHalfWidth / g.radius));
const arcLength = (g) => g.radius * 2 * halfAngle(g);
function arcPath(g, size, m) {
  const half = halfAngle(g), cap = (m.capHeight / m.unitsPerEm) * size;
  const cy = g.topAnchorY + cap + g.radius, dx = g.radius * Math.sin(half), y = cy - g.radius * Math.cos(half);
  return "M " + r2(g.cx-dx) + " " + r2(y) + " A " + r2(g.radius) + " " + r2(g.radius) + " 0 0 1 " + r2(g.cx+dx) + " " + r2(y);
}
const chordFor = (len, r) => 2 * r * Math.sin(Math.min(Math.PI, len/r) / 2);

function layoutShirtName(text, g, m) {
  const available = arcLength(g) * ARC_FILL_LIMIT;
  const taper = Math.max(0.5, 1 - (g.taperPerLetter || 0) * Math.max(0, text.length - 2));
  const maxForLength = Math.max(g.minFontSize, g.maxFontSize * taper);
  const describe = (size, tracking, len, fits, reason) => {
    const cap = (m.capHeight / m.unitsPerEm) * size;
    return { fits, reason, text, fontSize: size, tracking, pathD: arcPath(g, size, m), textLength: len,
             topY: g.topAnchorY, baselineY: g.topAnchorY + cap, maxForLength };
  };
  if (!text.length) return describe(g.maxFontSize, g.tracking, 0, false, "Enter a name for the shirt");
  for (let size = maxForLength; size >= g.minFontSize; size -= 1) {
    const w = measureText(text, m, size, g.tracking); if (w <= available) return describe(size, g.tracking, w, true);
  }
  for (let t = g.tracking; t >= 0; t -= 0.25) {
    const w = measureText(text, m, g.minFontSize, t); if (w <= available) return describe(g.minFontSize, t, w, true);
  }
  return describe(g.minFontSize, 0, measureText(text, m, g.minFontSize, 0), false,
    "That name is too long to fit across the shirt - please try a shorter one");
}
function layoutShirtNumber(value, g, m) {
  const text = String(value); let size = g.maxFontSize;
  while (size > g.minFontSize && measureText(text, m, size, 0) > g.maxWidth) size -= 1;
  return { text, fontSize: size, x: g.cx, baselineY: g.baselineY };
}
const escapeXml = (v) => v.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&apos;");

function shirtGeometry(shirt) {
  const w = shirt.right - shirt.left, h = shirt.bottom - shirt.top;
  return {
    name: { cx: shirt.centreX, chordHalfWidth: w*0.42, radius: w*0.8, topAnchorY: shirt.top + h*0.16,
            maxFontSize: h*0.21, minFontSize: h*0.075, tracking: w*0.005, taperPerLetter: 0.03 },
    number: { cx: shirt.centreX, baselineY: shirt.top + h*0.8, maxFontSize: h*0.51, minFontSize: h*0.33, maxWidth: w*0.78 }
  };
}

/* ---------- state ---------- */

/* ---------- drawing ---------- */

const canvas = document.getElementById("pp-canvas");
const ctx = canvas.getContext("2d", { willReadFrequently: true });
const figureCanvas = document.createElement("canvas");
const figureCtx = figureCanvas.getContext("2d", { willReadFrequently: true });
const hairCanvas = document.getElementById("pp-hair-over");
const hairCtx = hairCanvas.getContext("2d", { willReadFrequently: true });
const loaded = new Map();

function loadImage(key, src) {
  return new Promise((resolve) => {
    if (loaded.has(key)) return resolve(loaded.get(key));
    const img = new Image();
    img.onload = () => {
      const off = document.createElement("canvas");
      off.width = img.naturalWidth; off.height = img.naturalHeight;
      const octx = off.getContext("2d", { willReadFrequently: true });
      octx.drawImage(img, 0, 0);
      const entry = { img, data: octx.getImageData(0, 0, off.width, off.height) };
      loaded.set(key, entry);
      resolve(entry);
    };
    img.src = src;
  });
}

let outData = null;
let hairData = null;
async function drawPoster(figure) {
  const { data: source } = await loadImage(figure.code, figure.src);
  const w = source.width, h = source.height;
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w; canvas.height = h;
    figureCanvas.width = w; figureCanvas.height = h;
    hairCanvas.width = w; hairCanvas.height = h;
    outData = ctx.createImageData(w, h);
    hairData = hairCtx.createImageData(w, h);
  }

  const geo = { shirt: DATA.shirt, hemY: DATA.hemY, hemCurve: DATA.hemCurve, sleevesY: DATA.sleevesY, socksY: DATA.socksY, offsetX: 0, offsetY: 0, scale: 1 };
  const plan = figurePlan(figure.code, source, figure.keys, Math.max(5, Math.round(w * HAIR_MASK_REACH)));
  repaint(source, outData, plan, figure.pivots, paintOptions(), geo);
  figureCtx.putImageData(outData, 0, 0);

  // the hair again, on its own, to sit over the lettering
  const hairAlpha = plan.hairAlpha;
  const hd = hairData.data, od = outData.data;
  for (let p = 0; p < hairAlpha.length; p++) {
    const i = p * 4;
    hd[i] = od[i]; hd[i+1] = od[i+1]; hd[i+2] = od[i+2];
    hd[i+3] = Math.min(od[i+3], hairAlpha[p]);
  }
  hairCtx.putImageData(hairData, 0, 0);

  const bg = find(DATA.backgrounds, state.background);
  ctx.clearRect(0, 0, w, h);
  if (bg.mode === "scene") {
    const scene = DATA.scenes.find((s) => s.code === bg.scene);
    if (scene) {
      const { img } = await loadImage(scene.code, scene.src);
      ctx.drawImage(img, 0, 0, w, h);
    }
  } else {
    ctx.fillStyle = find(DATA.papers, state.paper).hex;
    ctx.fillRect(0, 0, w, h);
  }
  ctx.drawImage(figureCanvas, 0, 0);

  // cream stock tints the whole sheet, as printing on tinted paper would
  const paper = find(DATA.papers, state.paper);
  if (paper.hex.toUpperCase() !== "#FFFFFF" && bg.mode === "scene") {
    ctx.save();
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = paper.hex;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }
}

/*
 * Head-and-shoulders tiles, repainted so they show the chosen skin and hair
 * colour. Repainted from a head-sized crop rather than the whole sheet: eight
 * of these are redrawn every time a colour changes, and at full size that cost
 * more than the poster itself.
 */
const TILE = 128;
const TILE_SOURCE = 176;
const tileSources = new Map();

function tileSource(figure, source) {
  const cached = tileSources.get(figure.code);
  if (cached) return cached;
  const cropW = Math.round(DATA.width * 0.46), cropX = Math.round(DATA.width * 0.27), cropY = Math.round(DATA.height * 0.05);
  const small = document.createElement("canvas");
  small.width = TILE_SOURCE; small.height = TILE_SOURCE;
  const sctx = small.getContext("2d", { willReadFrequently: true });
  const full = document.createElement("canvas");
  full.width = source.width; full.height = source.height;
  full.getContext("2d").putImageData(source, 0, 0);
  sctx.drawImage(full, cropX, cropY, cropW, cropW, 0, 0, TILE_SOURCE, TILE_SOURCE);
  const entry = { data: sctx.getImageData(0, 0, TILE_SOURCE, TILE_SOURCE) };
  entry.geo = { shirt: DATA.shirt, hemY: DATA.hemY, hemCurve: DATA.hemCurve, sleevesY: DATA.sleevesY, socksY: DATA.socksY, offsetX: cropX, offsetY: cropY, scale: TILE_SOURCE / cropW };
  // The crop is not read apart on its own: shrinking it blends colours at
  // every edge, and its hair region came out smaller than the poster's. Every
  // pixel of the tile takes its decisions from the full-size analysis instead,
  // so the tile can only ever show what the poster shows.
  const poster = figurePlan(figure.code, source, figure.keys, Math.max(5, Math.round(source.width * HAIR_MASK_REACH)));
  const n = TILE_SOURCE * TILE_SOURCE;
  const plan = { key: new Uint8Array(n), second: new Uint8Array(n), mix: new Uint8Array(n), strength: new Uint8Array(n),
                 lum: new Uint8Array(n), sat: new Uint8Array(n), hairAlpha: new Uint8Array(n), width: TILE_SOURCE, height: TILE_SOURCE };
  for (let ty = 0; ty < TILE_SOURCE; ty++) {
    const sy = Math.min(source.height - 1, Math.floor(cropY + (ty + 0.5) / entry.geo.scale));
    for (let tx = 0; tx < TILE_SOURCE; tx++) {
      const sx = Math.min(source.width - 1, Math.floor(cropX + (tx + 0.5) / entry.geo.scale));
      const from = sy * source.width + sx, to = ty * TILE_SOURCE + tx;
      plan.key[to] = poster.key[from]; plan.second[to] = poster.second[from]; plan.mix[to] = poster.mix[from];
      plan.strength[to] = poster.strength[from]; plan.lum[to] = poster.lum[from]; plan.sat[to] = poster.sat[from];
    }
  }
  entry.plan = plan;
  tileSources.set(figure.code, entry);
  return entry;
}

async function drawHairTile(tileCanvas, figure) {
  const { data: source } = await loadImage(figure.code, figure.src);
  const small = tileSource(figure, source);
  tileCanvas.width = TILE; tileCanvas.height = TILE;
  const tctx = tileCanvas.getContext("2d", { willReadFrequently: true });

  const painted = tctx.createImageData(TILE_SOURCE, TILE_SOURCE);
  const opts = paintOptions();
  opts.splash = { mode: "none" };
  repaint(small.data, painted, small.plan, figure.pivots, opts, small.geo);

  const out = document.createElement("canvas");
  out.width = TILE_SOURCE; out.height = TILE_SOURCE;
  out.getContext("2d").putImageData(painted, 0, 0);

  tctx.fillStyle = find(DATA.papers, state.paper).hex;
  tctx.fillRect(0, 0, TILE, TILE);
  tctx.drawImage(out, 0, 0, TILE, TILE);
}

function drawLettering() {
  const m = DATA.metrics, geo = shirtGeometry(DATA.shirt);
  const name = layoutShirtName(state.name, geo.name, m);
  const number = layoutShirtNumber(state.number, geo.number, m);
  const kit = find(DATA.kits, state.kit);
  const patterned = kit.pattern && kit.pattern !== "solid";
  const pale = luminance(kit.primary) > 0.65;
  const fill = patterned ? "#FFFFFF" : pale ? "#1A1A1A" : "#FFFFFF";
  const outline = patterned ? "#1A1A1A" : pale ? "#FFFFFF" : "#1A1A1A";
  const svg = document.getElementById("pp-lettering");
  svg.setAttribute("viewBox", "0 0 " + DATA.width + " " + DATA.height);
  svg.innerHTML =
    (name.fits ? '<defs><path id="arc" d="' + name.pathD + '"/></defs>' +
      '<text font-family="FWShirt" font-size="' + name.fontSize + '" letter-spacing="' + name.tracking +
      '" fill="' + fill + '" stroke="' + outline + '" stroke-width="' + Math.max(1, name.fontSize*0.07) +
      '" paint-order="stroke" stroke-linejoin="round" opacity="0.95"><textPath href="#arc" startOffset="50%" text-anchor="middle">' +
      escapeXml(name.text) + "</textPath></text>" : "") +
    '<text x="' + number.x + '" y="' + number.baselineY + '" font-family="FWShirt" font-size="' + number.fontSize +
    '" text-anchor="middle" fill="' + fill + '" stroke="' + outline + '" stroke-width="' + Math.max(1.5, number.fontSize*0.055) +
    '" paint-order="stroke" stroke-linejoin="round" opacity="0.95">' + escapeXml(number.text) + "</text>";
  return name;
}



/* ==========================================================================
   The page: what the customer chooses, what it costs, and putting it in the
   basket. Everything above this line draws; everything below decides.
   ========================================================================== */

const PRODUCT = window.PP_PRODUCT;
const money = (pence) => "£" + (pence / 100).toFixed(pence % 100 ? 2 : 0);

/* ---------- what is chosen ---------- */

const state = {
  hair: DATA.hairStyles[0].code, skin: DATA.skinTones[0].code, hairColour: DATA.hairColours[2].code,
  kit: DATA.kits[0].code, background: DATA.backgrounds[0].code, paper: "PAPER-WHITE",
  name: "OLIVER", number: 9, size: "SIZE-A4", frame: "FRM-UNFRAMED"
};

/*
 * A link can arrive with the design already made - ?kit=ENG&bg=england&size=A3 -
 * so an ad about an England shirt opens on an England shirt. A code, a label
 * or the short form all work. The name is only ever taken from the link, never
 * guessed, and the cheapest print is the starting point.
 */
(function fromTheLink() {
  const q = new URLSearchParams(location.search);
  // with no design in the link, both arms open on the England kit in the England stadium (Philip, 29 Sep);
  // a link carrying any design, size or variant keeps the defaults above for whatever it leaves out
  const designInLink = ["kit", "bg", "background", "skin", "hair", "haircol", "hairColour", "size", "frame", "variant", "name", "number"].some((k) => q.has(k));
  if (!designInLink) {
    if (DATA.kits.some((k) => k.code === "KIT-ENG")) state.kit = "KIT-ENG";
    if (DATA.backgrounds.some((b) => b.code === "SCN-ENGLAND")) state.background = "SCN-ENGLAND";
  }
  const codeIn = (list, raw, prefix) => {
    if (!raw) return null;
    const want = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const hit = list.find((o) => o.code.toUpperCase().replace(/[^A-Z0-9]/g, "") === want
      || o.code.toUpperCase().replace(/[^A-Z0-9]/g, "") === prefix + want
      || o.label.toUpperCase().replace(/[^A-Z0-9]/g, "") === want
      || (o.scene && o.scene.toUpperCase().replace(/[^A-Z0-9]/g, "") === "BG" + want));
    return hit ? hit.code : null;
  };
  state.kit = codeIn(DATA.kits, q.get("kit"), "KIT") || state.kit;
  state.background = codeIn(DATA.backgrounds, q.get("bg") || q.get("background"), "") || codeIn(DATA.backgrounds, q.get("bg") || q.get("background"), "SCN") || codeIn(DATA.backgrounds, q.get("bg") || q.get("background"), "BG") || state.background;
  state.skin = codeIn(DATA.skinTones, q.get("skin"), "SKN") || state.skin;
  state.hair = codeIn(DATA.hairStyles, q.get("hair"), "HR") || state.hair;
  state.hairColour = codeIn(DATA.hairColours, q.get("haircol") || q.get("hairColour"), "HRC") || state.hairColour;
  state.size = codeIn(DATA.sizes, q.get("size"), "SIZE") || state.size;
  // Shopping ads and Merchant Center land on ?variant=<id>: read size and frame from that variant's SKU (FWPP-A3-OAK)
  const variantId = q.get("variant");
  if (variantId && window.PP_PRODUCT && Array.isArray(window.PP_PRODUCT.variants)) {
    const v = window.PP_PRODUCT.variants.find((x) => String(x.id) === String(variantId));
    if (v && v.sku) { const parts = v.sku.split("-"); const sz = codeIn(DATA.sizes, parts[1], "SIZE"); if (sz) state.size = sz; const fr = parts[2] === "NONE" ? "FRM-UNFRAMED" : "FRM-" + parts[2]; if (DATA.frames && DATA.frames.some((f) => f.code === fr)) state.frame = fr; }
  }
  state.frame = codeIn(DATA.frames, q.get("frame"), "FRM") || state.frame;
  if (q.get("name")) state.name = q.get("name");
  if (q.get("number")) state.number = Math.min(99, parseInt(q.get("number"), 10) || 0);
})();

const find = (list, code) => list.find((e) => e.code === code) || list[0];
const figureFor = (hair) => DATA.figures.find((f) => f.code === hair) || DATA.figures[0];
const normaliseName = (raw) => raw.normalize("NFC").replace(/[^\p{L} '-]/gu, "").replace(/\s+/g, " ").toUpperCase().slice(0, 12);

function paintOptions() {
  const kit = find(DATA.kits, state.kit);
  const bg = find(DATA.backgrounds, state.background);
  return {
    kit: { primary: kit.primary, secondary: kit.secondary, pattern: kit.pattern, sleeves: kit.sleeves, shorts: kit.shorts, socks: kit.socks },
    hair: find(DATA.hairColours, state.hairColour).hex,
    skin: find(DATA.skinTones, state.skin).hex,
    // a painted scene stands in for the ground, so the splash comes off
    splash: bg.mode === "scene" || bg.mode === "none" ? { mode: "none" } : { mode: bg.mode, colour: bg.hex }
  };
}

/* ---------- what was bought: the variant for this size and frame ---------- */

const FRAME_WORD = { "FRM-UNFRAMED": "Poster Only", "FRM-BLACK": "Black Framed", "FRM-WHITE": "White Framed", "FRM-OAK": "Oak Framed" };
function variantFor(size, frame) {
  const sizeLabel = find(DATA.sizes, size).label;
  const word = FRAME_WORD[frame];
  return PRODUCT.variants.find((v) => v.title.startsWith(sizeLabel + " ") && v.title.indexOf(word) >= 0) || null;
}
const priceFor = (size, frame) => { const v = variantFor(size, frame); return v ? v.price : 0; };

/* ---------- the controls ---------- */

const $ = (id) => document.getElementById(id);

function tiles(hostId, entries, key, render) {
  const host = $(hostId); host.innerHTML = "";
  for (const entry of entries) {
    const btn = document.createElement("button");
    btn.type = "button"; btn.className = "tile"; btn.setAttribute("role", "radio"); btn.dataset.code = entry.code;
    btn.setAttribute("aria-label", entry.label); btn.title = entry.title || entry.label;
    btn.innerHTML = render(entry) + (entry.showLabel === false ? "" : '<span class="cap">' + entry.label + "</span>");
    btn.addEventListener("click", () => { state[key] = entry.code; refresh(); });
    host.appendChild(btn);
  }
}
function mark(hostId, code) {
  for (const btn of $(hostId).children) btn.setAttribute("aria-checked", String(btn.dataset.code === code));
}
const chip = (hex) => '<span class="chip" style="background:' + hex + '"></span>';
function kitSwatch(kit) {
  if (kit.pattern === "stripes") return '<span class="shirt" style="background:repeating-linear-gradient(90deg,' + kit.primary + ' 0 6px,' + kit.secondary + ' 6px 12px)"></span>';
  if (kit.pattern === "hoops")   return '<span class="shirt" style="background:repeating-linear-gradient(180deg,' + kit.primary + ' 0 6px,' + kit.secondary + ' 6px 12px)"></span>';
  if (kit.pattern === "pinstripes") return '<span class="shirt" style="background:repeating-linear-gradient(90deg,' + kit.primary + ' 0 7px,' + kit.secondary + ' 7px 9px)"></span>';
  if (kit.pattern === "fade") return '<span class="shirt" style="background:linear-gradient(180deg,' + kit.primary + ',' + kit.secondary + ')"></span>';
  return '<span class="shirt" style="background:' + kit.primary + '"></span>';
}
function backgroundSwatch(bg) {
  if (bg.mode === "none") return '<span class="shirt none"></span>';
  if (bg.mode === "rainbow") return '<span class="shirt" style="background:linear-gradient(90deg,#e8384f,#f6a623,#f2e340,#4cbb5f,#3b8beb,#7a4fc9)"></span>';
  if (bg.mode === "scene") {
    const scene = DATA.scenes.find((s) => s.code === bg.scene);
    const thumb = (window.PP_ASSETS && PP_ASSETS.sceneThumbs && PP_ASSETS.sceneThumbs[bg.scene]) || (scene && scene.src);
    return scene ? '<img class="shirt" alt="" loading="lazy" decoding="async" width="160" height="90" src="' + thumb + '" style="object-fit:cover">' : '<span class="shirt"></span>';
  }
  return '<span class="shirt" style="background:' + bg.hex + '"></span>';
}

/* Kits and backgrounds come in families; one family shows at a time. */
const KIT_GROUPS = [
  ["Clubs", (k) => /^KIT-[A-Z]{3}$/.test(k.code) && !["KIT-ENG", "KIT-SCO", "KIT-WAL", "KIT-IRL", "KIT-BLK"].includes(k.code)],
  ["Nations", (k) => ["KIT-ENG", "KIT-SCO", "KIT-WAL", "KIT-IRL"].includes(k.code)],
  ["Colours", (k) => !/^KIT-[A-Z]{3}$/.test(k.code) || k.code === "KIT-BLK"],
];
const BG_GROUPS = [
  ["Stadiums", (b) => b.mode === "scene"],
  ["Colour splash", (b) => b.mode === "colour" || b.mode === "rainbow" || b.mode === "none"],
];
function groupedTiles(tabsId, hostId, groups, list, key, render) {
  const tabs = $(tabsId);
  const current = list.find((o) => o.code === state[key]);
  let active = groups.findIndex(([, test]) => current && test(current));
  if (active < 0) active = 0;
  const show = (index) => {
    tabs.innerHTML = "";
    groups.forEach(([label, test], i) => {
      const tab = document.createElement("button");
      tab.type = "button"; tab.className = "pp-tab"; tab.setAttribute("role", "tab"); tab.textContent = label;
      tab.setAttribute("aria-selected", String(i === index));
      tab.addEventListener("click", () => show(i));
      tabs.appendChild(tab);
    });
    tiles(hostId, list.filter(groups[index][1]), key, render);
    mark(hostId, state[key]);
  };
  show(active);
}

/* ---------- size and framing ---------- */

/*
 * The size and frame cards are built once and then updated in place (the ticked card and the price labels), never rebuilt on a click.
 * Rebuilding them on every click made the whole section flash, and moved the price block below it while the frame pictures reloaded (13 Oct).
 * The frame cards are only rebuilt when the Frame / No frame tab changes, because that shows a different set of cards.
 */
let sizeBuilt = false;
function buildSizeCards() {
  const host = $("pp-size");
  if (!sizeBuilt) {
    sizeBuilt = true; host.innerHTML = "";
    // sheets drawn to scale against the largest size, so A4 looks a quarter of A1 rather than a tiny room scene
    const biggest = Math.max(...DATA.sizes.map((s) => s.heightMm));
    const inches = (mm) => (mm / 25.4).toFixed(1);
    for (const size of DATA.sizes) {
      const card = document.createElement("button");
      card.type = "button"; card.className = "pp-size-card"; card.setAttribute("role", "radio"); card.dataset.code = size.code;
      const pct = Math.round((size.heightMm / biggest) * 88);
      card.innerHTML = '<span class="pp-size-up" hidden></span>'
        + '<span class="pp-paper-box"><span class="pp-paper" style="width:' + Math.round(pct * size.widthMm / size.heightMm) + '%;aspect-ratio:' + size.widthMm + ' / ' + size.heightMm + '">' + size.label + "</span></span>"
        + '<span class="pp-size-title">' + size.label + "</span>"
        + '<span class="pp-size-sub">' + size.widthMm / 10 + " &times; " + size.heightMm / 10 + " cm<br>" + inches(size.widthMm) + " &times; " + inches(size.heightMm) + " in</span>";
      card.addEventListener("click", () => { state.size = size.code; refresh(); });
      host.appendChild(card);
    }
  }
  const cheapest = priceFor("SIZE-A4", state.frame);
  for (const card of host.children) {
    const code = card.dataset.code, price = priceFor(code, state.frame), up = price - cheapest;
    const badge = card.querySelector(".pp-size-up");
    const text = up > 0 ? "+" + money(up) : "";
    if (badge.textContent !== text) badge.textContent = text;
    badge.hidden = !text;
  }
}
/*
 * Framing works like the fixture poster page: the Frame tab is open from the start so
 * the three frames (and what they add) are the first thing seen, while the print on its
 * own stays selected so the price shown is the lowest. The tabs only change what is shown;
 * a card has to be clicked to change what is bought, and clicking the chosen frame again
 * goes back to the print.
 */
let frameTab = "frame", frameBuiltTab = null;
function buildFrameCards() {
  const host = $("pp-frame");
  const showFrames = frameTab === "frame";
  if (frameBuiltTab !== frameTab) {
    frameBuiltTab = frameTab; host.innerHTML = "";
    for (const tab of document.querySelectorAll("#pp-frame-panel .pp-frame-tab")) {
      tab.setAttribute("aria-selected", String(tab.dataset.frametab === frameTab));
      tab.onclick = () => { frameTab = tab.dataset.frametab; buildFrameCards(); mark("pp-frame", state.frame); };
    }
    const options = showFrames ? DATA.frames.filter((f) => f.code !== "FRM-UNFRAMED") : DATA.frames.filter((f) => f.code === "FRM-UNFRAMED");
    host.classList.toggle("is-single", !showFrames);
    for (const frame of options) {
      const card = document.createElement("button");
      card.type = "button"; card.className = "pp-frame-opt"; card.setAttribute("role", "radio"); card.dataset.code = frame.code;
      // the black frame photo already carries its own "Bestseller" label, as on the fixture poster page; the picture has its size so the card never collapses while it loads
      card.innerHTML = '<img src="' + window.PP_ASSETS.frames[frame.code] + '" alt="' + frame.label + '" width="500" height="500" decoding="async">'
        + '<span class="pp-frame-label"><span class="pp-frame-name">' + (frame.code === "FRM-UNFRAMED" ? "Print only" : frame.label) + "</span> <em hidden></em></span>";
      card.addEventListener("click", () => {
        state.frame = (frame.code !== "FRM-UNFRAMED" && state.frame === frame.code) ? "FRM-UNFRAMED" : frame.code;
        refresh();
      });
      host.appendChild(card);
    }
  }
  // what a frame adds is shown while the print is chosen, and hidden once a frame is
  const printOnly = priceFor(state.size, "FRM-UNFRAMED");
  const frameChosen = state.frame !== "FRM-UNFRAMED";
  for (const card of host.children) {
    const code = card.dataset.code, em = card.querySelector("em");
    const text = !frameChosen && code !== "FRM-UNFRAMED" && priceFor(state.size, code) - printOnly > 0 ? "+" + money(priceFor(state.size, code) - printOnly) : "";
    if (em.textContent !== text) em.textContent = text;
    em.hidden = !text;
  }
}

/*
 * Free shipping countdown, as on the fixture poster page: UK Standard Delivery is free
 * from £40 (the shipping rate's own threshold), so show how far this print is from it,
 * amber with a progress bar while short, green once reached. Like the fixture page it
 * counts the item being designed, not what is already in the basket.
 */
const FREE_SHIP = 4000; // pence
function updateShipping(pence) {
  const el = $("pp-ship-msg");
  if (!el) return;
  // only touch the page when the message changed (rewriting it every click made the block flash)
  const set = (html, unlocked) => { if (el.dataset.html !== html) { el.innerHTML = html; el.dataset.html = html; } el.classList.toggle("is-unlocked", unlocked); };
  if (pence == null) { set("", false); return; }
  if (pence >= FREE_SHIP) { set("🎉 You've unlocked <strong>FREE SHIPPING</strong>", true); return; }
  const pct = Math.min(100, Math.round((pence / FREE_SHIP) * 100));
  set("You're only <strong>" + money(FREE_SHIP - pence) + "</strong> away from <strong>FREE SHIPPING</strong>"
    + '<span class="pp-ship-track"><span class="pp-ship-fill" style="width:' + pct + '%"></span></span>', false);
}

/* ---------- keeping everything in step ---------- */

let pending = false, tiledFor = "";
async function refresh() {
  if (pending) return;
  pending = true;
  requestAnimationFrame(async () => {
    pending = false;
    const figure = figureFor(state.hair);
    await drawPoster(figure);
    const name = drawLettering();

    const tileKey = state.skin + "|" + state.hairColour + "|" + state.kit;
    if (tiledFor !== tileKey) {
      tiledFor = tileKey;
      tiles("pp-hair", DATA.hairStyles, "hair", () => '<canvas class="hair-tile" width="128" height="128"></canvas>');
      queueHairTiles();
    }

    const frameClass = state.frame === "FRM-BLACK" ? "framed-black" : state.frame === "FRM-WHITE" ? "framed-white" : state.frame === "FRM-OAK" ? "framed-oak" : "unframed";
    for (const p of document.querySelectorAll(".poster")) p.className = "poster " + frameClass;
    for (const k of ["skin", "hair", "hairColour", "kit", "background"]) mark("pp-" + k, state[k]);
    buildSizeCards(); mark("pp-size", state.size);
    buildFrameCards(); mark("pp-frame", state.frame);

    $("pp-background-note").textContent = find(DATA.backgrounds, state.background).note || "";
    const note = $("pp-name-note"), input = $("pp-name");
    if (!name.fits) { note.textContent = name.reason; note.className = "pp-note bad"; input.setAttribute("aria-invalid", "true"); }
    else {
      note.textContent = state.name.length <= 2 ? "Short and bold - " + state.name + " prints at full size."
        : name.fontSize >= name.maxForLength - 0.5 ? "" : "A long one - we shrink it a touch so it fits the shirt, like a real one.";
      note.className = "pp-note"; input.removeAttribute("aria-invalid");
    }

    // price, summary, basket
    const variant = variantFor(state.size, state.frame);
    const price = variant ? money(variant.price) : "";
    const sizeLabel = find(DATA.sizes, state.size).label;
    const frameLabel = state.frame === "FRM-UNFRAMED" ? "print only" : find(DATA.frames, state.frame).label + " frame";
    const player = state.name + " #" + state.number;
    if ($("pp-value-sub")) $("pp-value-sub").textContent = player + " · " + sizeLabel + " · " + frameLabel;
    if ($("pp-value-price")) $("pp-value-price").textContent = price;
    $("pp-price").textContent = price;
    updateShipping(variant ? variant.price : null);
    $("pp-sticky-summary").textContent = player + " · " + sizeLabel + " · " + frameLabel; $("pp-sticky-price").textContent = price;
    $("pp-sheet-summary").textContent = player; $("pp-sheet-price").textContent = price;
    if ($("pp-summary-player")) {
      $("pp-summary-player").textContent = player;
      $("pp-summary-rest").textContent = find(DATA.kits, state.kit).label + " kit · " + find(DATA.backgrounds, state.background).label + " · " + find(DATA.hairStyles, state.hair).label.toLowerCase() + " hair";
    }
    $("pp-atc").disabled = !name.fits || !variant || !variant.available;
    $("pp-sticky-btn").disabled = $("pp-atc").disabled;
    if ($("pp-combos")) $("pp-combos").textContent = DATA.combinations.toLocaleString("en-GB") + " possible players. Only one of them is theirs.";
    queueThumb();
    if (typeof queueWall === "function") queueWall();
    designSettled();
    ppSettled();
  });
}

/* ---------- putting it in the basket ---------- */

function designParams() {
  const design = { skin: state.skin, hair: state.hair, hairColour: state.hairColour, kit: state.kit, background: state.background, paper: state.paper, name: state.name, number: state.number, size: state.size, frame: state.frame };
  const p = new URLSearchParams(); for (const [k, v] of Object.entries(design)) p.set(k, String(v));
  return p;
}
// the picture's key leaves out size and frame: every A size has the same shape and the frame is not in the painting,
// so changing either never forces a new paint and upload (d8, 29 Sep)
// the basket picture: the same painting at every size, with the chosen frame drawn round it (fw-pp-thumb)
function pictureKey(frame) { const p = designParams(); p.set("size", "SIZE-A4"); if (frame) p.set("frame", frame); return p.toString(); }
function properties(preview) {
  const props = {
    "Name on shirt": state.name, "Number": String(state.number),
    "Kit": find(DATA.kits, state.kit).label, "Background": find(DATA.backgrounds, state.background).label,
    "Hair": find(DATA.hairStyles, state.hair).label + ", " + find(DATA.hairColours, state.hairColour).label.toLowerCase(),
    "Skin tone": find(DATA.skinTones, state.skin).label,
    "_fw_design": designParams().toString(),
  };
  // the painted design on cdn.shopify.com: the basket and checkout show it in place of the product photo
  if (preview) props["_fw_preview"] = preview;
  return props;
}

/*
 * The design's picture for the basket and checkout. Checkout only shows a picture hosted on
 * cdn.shopify.com, so the fw-pp-thumb worker paints the design small and copies it into the shop's
 * Files: about 9 seconds the first time, instant after. To have it ready when the basket opens, the
 * picture is painted in the background whenever the design settles, and copied to Shopify once the
 * shopper has reached size, frame or Add to cart (so browsing alone does not fill Files).
 */
const THUMB = "https://fw-pp-thumb.vitalyz.workers.dev";
const cdnJobs = new Map();
/*
 * A failure is never remembered (13 Oct 2026, order #1712155 reached checkout with no picture because one failed request was
 * kept as the answer for the whole visit). Each request has a time limit, so one that hangs cannot stick either; a job tries
 * three times, two seconds apart, and if all fail it is dropped from the map so the next ask starts afresh.
 */
const CDN_TRIES = 3, CDN_LIMIT_MS = 25000, CDN_GAP_MS = 2000;
function cdnOnce(key) {
  const ctl = typeof AbortController === "function" ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), CDN_LIMIT_MS) : 0;
  return fetch(THUMB + "/pp-cdn?" + key, ctl ? { signal: ctl.signal } : undefined)
    .then((r) => (r.ok ? r.json() : null)).then((j) => (j && j.url) || null).catch(() => null)
    .then((url) => { clearTimeout(timer); return url; });
}
function cdnFor(key) {
  if (!cdnJobs.has(key)) {
    const job = (async () => {
      for (let i = 0; i < CDN_TRIES; i++) {
        const url = await cdnOnce(key);
        if (url) return url;
        if (i < CDN_TRIES - 1) await new Promise((r) => setTimeout(r, CDN_GAP_MS));
      }
      return null;
    })().then((url) => { if (!url) cdnJobs.delete(key); return url; });
    cdnJobs.set(key, job);
  }
  return cdnJobs.get(key);
}
// for the basket line: keep asking (three rounds of the above) until the picture arrives
async function cdnPatient(key, rounds = 3) {
  for (let i = 0; i < rounds; i++) { const url = await cdnFor(key); if (url) return url; }
  return null;
}
/*
 * Meta's CustomizeProduct, for "designed but did not buy" audiences (pixel tab, 13 Oct 2026). Two stages, each once per
 * page view: "started" (the first real change to the design) and "size_frame" (they have changed the design and the
 * size, frame or Add to cart are on screen). The browser sends it through the Facebook & Instagram app's pixel (fbq is on
 * the page) and the hub sends the same event through the Conversions API with the same event_id, so Meta counts it once.
 * ?lp_test=1 visits go to the hub flagged as test, which sends them to Meta's Test Events only.
 */
const metaSent = {};
function metaCustomize(stage) {
  if (metaSent[stage]) return;
  metaSent[stage] = true;
  try {
    const variant = variantFor(state.size, state.frame);
    const ids = variant ? [String(variant.id)] : [];
    const eid = "cp-" + (window.crypto && crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2));
    if (typeof window.fbq === "function") window.fbq("track", "CustomizeProduct", { content_type: "product", content_ids: ids, pd_stage: stage }, { eventID: eid });
    // the Google bridge in layout/theme.liquid listens for this (no names or pictures: an id and a stage only)
    document.dispatchEvent(new CustomEvent("pp:track", { detail: { name: stage === "started" ? "pd_design_started" : "pd_size_frame", ids, eid } }));
    const cookie = (n) => { const m = document.cookie.match(new RegExp("(?:^|; )" + n + "=([^;]*)")); return m ? decodeURIComponent(m[1]) : ""; };
    const y = document.querySelector('meta[name="shopify-y"]');
    const body = JSON.stringify({ eid, stage, ids, url: location.origin + location.pathname, fbp: cookie("_fbp"), fbc: cookie("_fbc"), vid: (window.LP && LP.vid) || "", y: y ? y.content : "", test: !!(window.LP && LP.test) });
    const url = "https://fw-hub.vercel.app/api/meta/customize";
    if (!(navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: "text/plain" })))) fetch(url, { method: "POST", body, keepalive: true, headers: { "content-type": "text/plain" } }).catch(() => {});
  } catch (e) {}
}
function metaSizeFrame() { if (nearBuying && metaSent.started) metaCustomize("size_frame"); }
/*
 * A saved design (job 3, save and restore): the page that saves it sets window.PP_DESIGN_HASH and dispatches
 * document "pp:design-saved" with detail { hash }. That sends ViewContent with content_ids [hash], which is what lets Meta
 * match the person to their own catalogue item for dynamic product ads. Browser and hub share the event_id; once per hash.
 * The hash is the design's config hash only: no name, number or picture.
 */
const metaSavedHashes = {};
function metaDesignSaved(hash) {
  hash = String(hash || "");
  if (!/^[0-9a-zA-Z_-]{6,64}$/.test(hash) || metaSavedHashes[hash]) return;
  metaSavedHashes[hash] = true;
  try {
    const variant = variantFor(state.size, state.frame);
    const value = variant ? Number(variant.price) / 100 : 0;
    const eid = "cp-" + (window.crypto && crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2));
    const data = { content_type: "product", content_ids: [hash] };
    if (value > 0) { data.value = value; data.currency = "GBP"; }
    if (typeof window.fbq === "function") window.fbq("track", "ViewContent", data, { eventID: eid });
    document.dispatchEvent(new CustomEvent("pp:track", { detail: { name: "pd_design_saved", ids: [hash], value, eid } }));
    const cookie = (n) => { const m = document.cookie.match(new RegExp("(?:^|; )" + n + "=([^;]*)")); return m ? decodeURIComponent(m[1]) : ""; };
    const y = document.querySelector('meta[name="shopify-y"]');
    const body = JSON.stringify({ eid, stage: "saved", ids: [hash], value, url: location.origin + location.pathname, fbp: cookie("_fbp"), fbc: cookie("_fbc"), vid: (window.LP && LP.vid) || "", y: y ? y.content : "", test: !!(window.LP && LP.test) });
    const url = "https://fw-hub.vercel.app/api/meta/customize";
    if (!(navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: "text/plain" })))) fetch(url, { method: "POST", body, keepalive: true, headers: { "content-type": "text/plain" } }).catch(() => {});
  } catch (e) {}
}
document.addEventListener("pp:design-saved", (e) => metaDesignSaved(e && e.detail && e.detail.hash));

let nearBuying = false, settleTimer = 0;
let firstPictureKey = null;
function designSettled() {
  clearTimeout(settleTimer);
  // once they are near Add to cart, every change is prepared almost at once, so the click can send it straight away
  settleTimer = setTimeout(() => {
    const key = pictureKey();
    if (firstPictureKey === null) firstPictureKey = key;
    else if (key !== firstPictureKey) { metaCustomize("started"); metaSizeFrame(); }
    if (nearBuying) {
      // this frame first, then the other three, so switching frame shows the right picture straight away
      cdnFor(key).then(() => { for (const f of DATA.frames) if (f.code !== state.frame) cdnFor(pictureKey(f.code)); });
    } else if (key !== firstPictureKey) {
      // still designing: have the painting made and cached, without downloading it (204, no body); nothing on first load
      const warm = () => fetch(THUMB + "/pp-warm?" + key, { mode: "no-cors" }).catch(() => {});
      if (window.requestIdleCallback) requestIdleCallback(warm, { timeout: 2000 }); else warm();
    }
  }, nearBuying ? 300 : 1200);
}
try {
  const seen = new IntersectionObserver((entries) => {
    if (!nearBuying && entries.some((e) => e.isIntersecting)) { nearBuying = true; seen.disconnect(); designSettled(); metaSizeFrame(); }
  }, { rootMargin: "200px 0px" });
  for (const id of ["pp-size-panel", "pp-frame-panel", "pp-atc"]) if ($(id)) seen.observe($(id));
} catch (e) { nearBuying = true; }

async function addToCart(trigger) {
  const variant = variantFor(state.size, state.frame);
  const buttons = [$("pp-atc"), $("pp-sticky-btn")];
  const error = $("pp-atc-error");
  if (!variant) return;
  ppAddClickedAt = Date.now();
  error.hidden = true;
  for (const b of buttons) { b.disabled = true; b.textContent = "ADDING…"; }
  // ask Shopify for the cart drawer's freshly rendered items in the same call, as the theme's own product form does
  const drawer = document.querySelector("cart-drawer-component");
  const sectionIds = Array.from(document.querySelectorAll("cart-items-component")).map((el) => el.dataset.sectionId).filter(Boolean);
  // wait briefly for the design's picture; if it is not ready yet, add now and put it on the line when it is
  const designKey = pictureKey();
  const preview = await Promise.race([cdnFor(designKey), new Promise((r) => setTimeout(() => r(null), 1500))]);
  let patching = null; // the basket line's picture, put on once it arrives (see below)
  const props = properties(preview);
  const body = JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties: props }], sections: sectionIds.join(",") });
  const attempt = () => fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body });
  try {
    let res = await attempt();
    if (!res.ok) { await new Promise((r) => setTimeout(r, 1500)); res = await attempt(); }
    if (!res.ok) throw new Error("status " + res.status);
    const added = await res.json().catch(() => ({}));
    if (!preview) {
      const line = (added.items && added.items[0]) || null;
      patching = cdnPatient(designKey).then(async (url) => {
        if (!url || !line || !line.key) return;
        try {
          await fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: JSON.stringify({ id: line.key, quantity: line.quantity || 1, properties: properties(url) }) });
          const again = new Event("cart:update", { bubbles: true });
          again.detail = { resource: {}, sourceId: "pp-designer", data: { source: "pp-designer" } };
          document.dispatchEvent(again);
          // UpCart does not listen to cart:update: ask it to reload so the drawer shows the picture
          try { if (typeof window.upcartRefreshCart === "function") window.upcartRefreshCart(); } catch (e) {}
        } catch (e) {}
      });
    }
    // landing page test: the basket carries the arm, so the order is credited to it exactly (BANDIT-SPEC §2)
    // test ended (B won): the group id still goes on the basket, under its own experiment name so pd_lp_1's numbers stay as they were
    if (window.LP && (LP.record || LP.ended)) {
      const attributes = LP.ended
        ? { lp_exp: "pd_page_b", lp_arm: "B", lp_vid: LP.vid || "", lp_test: LP.test ? "1" : "" }
        : { lp_exp: LP.exp, lp_arm: LP.arm, lp_vid: LP.vid || "", lp_test: LP.test ? "1" : "" };
      try { await Promise.race([fetch("/cart/update.js", { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: JSON.stringify({ attributes }) }), new Promise((r) => setTimeout(r, 1500))]); } catch (e) {}
      LP.send("add_to_cart", { variant_id: variant.id, trigger });
    }
    try { if (window.posthog && posthog.capture) posthog.capture("pp_add_to_cart", { trigger, kit: state.kit, background: state.background, size: state.size, frame: state.frame, variant_id: variant.id, mode: $("pp").dataset.mode, lp_exp: window.LP && LP.exp, lp_arm: window.LP && LP.arm }); } catch (e) {}
    ppAfterAdd(variant);
    // going to the basket page ends this script: give a late picture a few seconds to reach the line first
    if (!drawer || typeof drawer.open !== "function") { if (patching) await Promise.race([patching, new Promise((r) => setTimeout(r, 6000))]); window.location.assign("/cart"); return; }
    // refresh the drawer and the basket count through the theme's own "cart:update" event, then slide the drawer open
    let itemCount = 0;
    try { itemCount = (await (await fetch("/cart.js", { headers: { "Accept": "application/json" } })).json()).item_count || 0; } catch (e) {}
    const update = new Event("cart:update", { bubbles: true });
    update.detail = { resource: {}, sourceId: "pp-designer", data: { source: "pp-designer", itemCount, variantId: String(variant.id), sections: added.sections || {} } };
    document.dispatchEvent(update);
    for (const b of buttons) { b.disabled = false; b.textContent = "ADD TO BASKET"; }
    drawer.open();
  } catch (e) {
    ppAddClickedAt = 0; // the add did not happen: the prompts may show again
    for (const b of buttons) { b.disabled = false; b.textContent = "ADD TO BASKET"; }
    error.textContent = "Sorry, that didn't add to your basket. Please tap ADD TO BASKET again."; error.hidden = false;
  }
}

/* ---------- layout: on the page, or in a sheet ---------- */

const root = $("pp");
// the frame band is a share of the print's own width: tell the CSS that width (a percentage would be measured against the wider container)
try {
  const posterWidth = new ResizeObserver((es) => { for (const e of es) e.target.style.setProperty("--pwx", (e.borderBoxSize && e.borderBoxSize[0] ? e.borderBoxSize[0].inlineSize : e.contentRect.width) + "px"); });
  for (const poster of document.querySelectorAll(".poster")) posterWidth.observe(poster);
} catch (e) {}
const designer = $("pp-designer");
const sheet = $("pp-sheet");
function openSheet() {
  $("pp-sheet-designer").appendChild(designer); designer.hidden = false;
  // the preview moves into the sheet with the designer, and back after
  $("pp-sheet-preview").appendChild($("pp-poster"));
  sheet.classList.add("is-open"); document.body.style.overflow = "hidden";
  history.pushState({ ppSheet: true }, "");
  ppDesignerOpened();
}
function closeSheet() {
  sheet.classList.remove("is-open"); document.body.style.overflow = "";
  document.querySelector("#pp-preview .pp-poster-wrap").appendChild($("pp-poster"));
  if (history.state && history.state.ppSheet) history.back();
  refresh();
  ppOnClose();
}
if (root.dataset.mode === "customise") {
  $("pp-customise").addEventListener("click", openSheet);
  $("pp-sheet-done").addEventListener("click", closeSheet);
  $("pp-sheet-done-2").addEventListener("click", closeSheet);
  window.addEventListener("popstate", () => { if (sheet.classList.contains("is-open")) closeSheet(); });
} else {
  $("pp-designer-home").appendChild(designer); designer.hidden = false;
}

/* ---------- landing page test: arm B opens on the finished print ---------- */

// "Customise yours" opens the designer, size, frame, price and Add to cart as a full-screen sheet over the page
// (the same elements arm A shows, so the design carries over both ways). Close (the X, Escape or the phone's back
// gesture) returns to the page with the design kept; the page's print is the same element, so it shows their design.
const html = document.documentElement;
const bOpen = () => html.classList.contains("lp-open");
const lpEvent = (name, extra) => {
  try { if (window.posthog && posthog.capture) posthog.capture(name, Object.assign({ lp_exp: window.LP && LP.exp, lp_arm: window.LP && LP.arm }, extra)); } catch (e) {}
  if (window.LP && LP.send) LP.send(name.replace(/^pp_/, ""), extra);
};
if ($("pp-b-price")) {
  // "From" the lowest price among the variants on sale, computed rather than typed
  const on = PRODUCT.variants.filter((v) => v.available);
  if (on.length) $("pp-b-price").textContent = money(Math.min.apply(null, on.map((v) => v.price)));
}
// the page behind stays where it was: the browser's own scroll restoration is paused while the sheet owns a history entry
let pageY = 0, restoreMode = "auto";
function keepPageY() { window.scrollTo(0, pageY); requestAnimationFrame(() => window.scrollTo(0, pageY)); setTimeout(() => { window.scrollTo(0, pageY); if ("scrollRestoration" in history) history.scrollRestoration = restoreMode; }, 60); }
// the basket count in the sheet's header, kept in step with the site's basket
function showCount(n) {
  const el = $("pp-b-cart-count"); if (!el) return;
  el.textContent = n > 99 ? "99+" : String(n); el.hidden = !(n > 0);
  $("pp-b-cart").setAttribute("aria-label", n > 0 ? "Open your basket, " + n + (n === 1 ? " item" : " items") : "Open your basket");
}
function refreshCount() { return fetch("/cart.js", { headers: { "Accept": "application/json" } }).then((r) => r.json()).then((c) => showCount(c.item_count || 0)).catch(() => {}); }
function openB(trigger) {
  if (bOpen()) return;
  pageY = window.scrollY;
  if ("scrollRestoration" in history) { restoreMode = history.scrollRestoration; history.scrollRestoration = "manual"; }
  html.classList.remove("lp-closing"); clearTimeout(closingTimer);
  html.classList.add("lp-open");
  root.setAttribute("role", "dialog"); root.setAttribute("aria-modal", "true"); root.setAttribute("aria-labelledby", "pp-b-title");
  history.pushState({ ppB: true }, "");
  ppDesignerOpened();
  lpEvent("pp_customise_open", trigger === "sticky" ? { trigger: "sticky" } : undefined);
  refresh(); setSticky(); refreshCount();
  $("pp-b-close").focus({ preventScroll: true });
}
let closingTimer = 0;
function closeB(fromHistory) {
  if (!bOpen() || html.classList.contains("lp-closing")) return;
  lpEvent("pp_customise_close");
  ppOnClose();
  if (!fromHistory && history.state && history.state.ppB) history.back();
  // slide away, then hand the page back
  html.classList.add("lp-closing");
  closingTimer = setTimeout(() => {
    html.classList.remove("lp-open", "lp-closing");
    root.removeAttribute("role"); root.removeAttribute("aria-modal"); root.removeAttribute("aria-labelledby");
    refresh(); setSticky();
    $("pp-b-customise").focus({ preventScroll: true });
    keepPageY();
  }, 200);
}
if ($("pp-b-customise")) {
  $("pp-b-customise").addEventListener("click", () => openB());
  // the sticky bottom bar (version B, page closed): same sheet, same event, trigger "sticky"
  if ($("pp-b-sticky-btn")) $("pp-b-sticky-btn").addEventListener("click", () => openB("sticky"));
  // the bar shows only once the in-page button has scrolled out of view, and hides again when it is back, so the two are never on screen together
  try { new IntersectionObserver((entries) => { const e = entries[entries.length - 1]; html.classList.toggle("pp-cta-away", !(e.isIntersecting && e.intersectionRatio >= 0.15)); }, { threshold: [0, 0.15, 0.5, 1] }).observe($("pp-b-customise")); }
  catch (e) { html.classList.add("pp-cta-away"); }
  $("pp-b-close").addEventListener("click", () => closeB(false));
  $("pp-b-scrim").addEventListener("click", () => closeB(false));
  window.addEventListener("popstate", () => closeB(true));
  // the basket button in the sheet's header: the theme's own cart drawer opens above the sheet; the count follows the site's
  document.addEventListener("cart:update", (e) => { const n = e.detail && e.detail.data && e.detail.data.itemCount; if (typeof n === "number") showCount(n); else refreshCount(); });
  // this page's basket is the UpCart app's drawer; the theme's own drawer and then /cart are the fallbacks
  const drawerEl = () => document.querySelector("cart-drawer-component");
  $("pp-b-cart").addEventListener("click", () => {
    const d = drawerEl();
    if (typeof window.upcartOpenCart === "function") window.upcartOpenCart();
    else if (d && typeof d.open === "function") d.open(); else window.location.assign("/cart");
    try { if (window.posthog && posthog.capture) posthog.capture("pp_sheet_cart_open", { lp_exp: window.LP && LP.exp, lp_arm: window.LP && LP.arm }); } catch (err) {}
  });
  // when the drawer closes (items may have been removed there), bring the count up to date; the sheet stays as it was
  document.addEventListener("close", (e) => { if (bOpen() && e.target && e.target.tagName === "DIALOG") refreshCount(); }, true);
  let upcartWasOpen = false;
  new MutationObserver(() => {
    const now = document.body.classList.contains("upcartPopupShow");
    if (upcartWasOpen && !now && bOpen()) refreshCount();
    upcartWasOpen = now;
  }).observe(document.body, { attributes: true, attributeFilter: ["class"] });
  // a basket drawer open above the sheet (UpCart, or a dialog of the theme's): it handles its own keys and focus
  const overlayOpen = () => document.body.classList.contains("upcartPopupShow") || Array.from(document.querySelectorAll("dialog[open]")).some((d) => !root.contains(d));
  document.addEventListener("keydown", (e) => {
    if (!bOpen() || overlayOpen()) return;
    if (e.key === "Escape") {
      // the size guide and "see it big" close first
      if ($("pp-guide").classList.contains("is-open")) { $("pp-guide").classList.remove("is-open"); return; }
      if ($("pp-big").classList.contains("is-open")) { $("pp-big").classList.remove("is-open"); return; }
      closeB(false); return;
    }
    if (e.key !== "Tab") return;
    // keep keyboard focus inside the sheet
    const f = Array.from(root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter((el) => !el.disabled && el.getClientRects().length && getComputedStyle(el).visibility !== "hidden");
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (!root.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
}


// The name and number are an SVG overlay drawn in the shirt font. Turned into a picture (the buy bar's thumbnail,
// "see it big", the room photos), an SVG cannot see the page's fonts, so the font is embedded in it, fetched once.
var shirtFontCss = null;
function shirtFont() {
  if (!shirtFontCss) {
    const url = window.PP_ASSETS && PP_ASSETS.font;
    shirtFontCss = !url ? Promise.resolve("") : fetch(url).then((r) => r.arrayBuffer()).then((buf) => {
      const bytes = new Uint8Array(buf); let bin = "";
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
      return '@font-face{font-family:"FWShirt";src:url(data:font/ttf;base64,' + btoa(bin) + ') format("truetype");}';
    }).catch(() => "");
  }
  return shirtFontCss;
}
function letteringSrc(w, h) {
  return shirtFont().then((css) => {
    let svg = new XMLSerializer().serializeToString($("pp-lettering")).replace("<svg ", '<svg width="' + w + '" height="' + h + '" ');
    if (css) svg = svg.replace(/(<svg[^>]*>)/, "$1<defs><style>" + css + "</style></defs>");
    return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  });
}
// the buy bar's thumbnail: the preview (art, lettering, hair over it) baked small, in the chosen frame; after changes settle
const FRAME_CLASS = { "FRM-BLACK": "framed-black", "FRM-WHITE": "framed-white", "FRM-OAK": "framed-oak", "FRM-UNFRAMED": "unframed" };
let thumbTimer = 0;
function queueThumb() { clearTimeout(thumbTimer); thumbTimer = setTimeout(drawThumb, 350); }
function drawThumb() {
  const box = $("pp-thumb"), c = $("pp-canvas");
  if (!box || !c || !c.width) return;
  box.className = "pp-thumb " + (FRAME_CLASS[state.frame] || "unframed");
  const out = document.createElement("canvas");
  out.width = 120; out.height = Math.round(120 * c.height / c.width);
  const g = out.getContext("2d"); g.drawImage(c, 0, 0, out.width, out.height);
  const finish = () => { try { g.drawImage($("pp-hair-over"), 0, 0, out.width, out.height); $("pp-thumb-img").src = out.toDataURL("image/jpeg", 0.85); } catch (e) {} };
  const img = new Image();
  img.onload = () => { g.drawImage(img, 0, 0, out.width, out.height); finish(); };
  img.onerror = finish;
  letteringSrc(c.width, c.height).then((src) => { img.src = src; });
}
// the art itself arrives a moment after a change: catch it too
setTimeout(queueThumb, 1500); setTimeout(queueThumb, 4000);

// see it big
if ($("pp-see-big")) $("pp-see-big").addEventListener("click", () => {
  const out = document.createElement("canvas");
  const c = $("pp-canvas"); out.width = c.width; out.height = c.height;
  const g = out.getContext("2d"); g.drawImage(c, 0, 0);
  // the lettering and the hair over it are drawn on the page as overlays; bake them in for the big view
  const img = new Image();
  img.onload = () => { g.drawImage(img, 0, 0, out.width, out.height); g.drawImage($("pp-hair-over"), 0, 0); $("pp-big-img").src = out.toDataURL("image/jpeg", 0.9); $("pp-big").classList.add("is-open"); };
  img.onerror = () => { g.drawImage($("pp-hair-over"), 0, 0); $("pp-big-img").src = out.toDataURL("image/jpeg", 0.9); $("pp-big").classList.add("is-open"); };
  letteringSrc(out.width, out.height).then((src) => { img.src = src; });
});
$("pp-big-close").addEventListener("click", () => $("pp-big").classList.remove("is-open"));
if ($("pp-size-guide")) {
  $("pp-size-guide").addEventListener("click", () => $("pp-guide").classList.add("is-open"));
  $("pp-guide-close").addEventListener("click", () => $("pp-guide").classList.remove("is-open"));
  $("pp-guide").addEventListener("click", (e) => { if (e.target === $("pp-guide")) $("pp-guide").classList.remove("is-open"); });
}
$("pp-big").addEventListener("click", (e) => { if (e.target === $("pp-big")) $("pp-big").classList.remove("is-open"); });

// the sticky bar shows whenever the real button is off screen
const sticky = $("pp-sticky");
let atcInView = true;
function setSticky() {
  const bClosed = html.classList.contains("lp-arm-b") && !bOpen();
  // inside B's sheet the buy bar is the pinned footer, always on
  const shown = bOpen() || (!atcInView && !sheet.classList.contains("is-open") && !bClosed);
  sticky.classList.toggle("is-on", shown); sticky.setAttribute("aria-hidden", String(!shown));
  document.body.classList.toggle("pp-sticky-active", shown && !bOpen()); // inside B's sheet the sheet keeps its own room for the bar
}
new IntersectionObserver((entries) => { atcInView = entries[0].isIntersecting; setSticky(); }, { threshold: 0 }).observe($("pp-atc"));

/* ---------- wiring ---------- */

tiles("pp-skin", DATA.skinTones, "skin", (e) => chip(e.hex));
tiles("pp-hairColour", DATA.hairColours, "hairColour", (e) => chip(e.hex));
groupedTiles("pp-kit-tabs", "pp-kit", KIT_GROUPS, DATA.kits, "kit", kitSwatch);
groupedTiles("pp-background-tabs", "pp-background", BG_GROUPS, DATA.backgrounds, "background", backgroundSwatch);

const nameInput = $("pp-name");
nameInput.addEventListener("input", () => {
  const cleaned = normaliseName(nameInput.value);
  if (cleaned !== nameInput.value) { const at = nameInput.selectionStart; nameInput.value = cleaned; try { nameInput.setSelectionRange(at, at); } catch (e) {} }
  state.name = cleaned; refresh();
});
const numberInput = $("pp-number");
numberInput.addEventListener("input", () => {
  const raw = numberInput.value.replace(/[^0-9]/g, "").slice(0, 2);
  if (raw !== numberInput.value) numberInput.value = raw;
  state.number = raw === "" ? 0 : Math.min(99, parseInt(raw, 10)); refresh();
});
numberInput.addEventListener("blur", () => { numberInput.value = String(state.number); });
$("pp-surprise").addEventListener("click", () => {
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
  state.hair = pick(DATA.hairStyles).code; state.skin = pick(DATA.skinTones).code; state.hairColour = pick(DATA.hairColours).code;
  state.kit = pick(DATA.kits).code; state.background = pick(DATA.backgrounds).code;
  groupedTiles("pp-kit-tabs", "pp-kit", KIT_GROUPS, DATA.kits, "kit", kitSwatch);
  groupedTiles("pp-background-tabs", "pp-background", BG_GROUPS, DATA.backgrounds, "background", backgroundSwatch);
  refresh();
});
$("pp-atc").addEventListener("click", () => addToCart("page"));
$("pp-sticky-btn").addEventListener("click", () => addToCart("sticky"));

state.name = normaliseName(state.name) || "OLIVER";
nameInput.value = state.name; numberInput.value = String(state.number);
refresh();

/* ==========================================================================
   Save and restore (13 Oct 2026). Every design is kept on the phone and in the hub; a visitor who comes back (a link in an
   email, the same browser, the same person on another device) lands on what they made; "Email me this design" and one
   smart prompt put it in their inbox; the exit survey waits its turn. The name on the shirt is a child's first name: it goes
   to the hub (kept 60 days) and into the design email, never into PostHog, Meta or Google as text.
   ?pp_qa=1 is a test switch: it ignores the once in 3 days limit and shortens the idle wait. It records nothing on its own.
   ========================================================================== */
const PP_HUB = "https://fw-hub.vercel.app/api/pd/";
const PP_KEY = { saved: "pp_design_v1", fresh: "pp_fresh", prompt: "pp_prompt_at", added: "pp_added", captured: "pp_captured" };
const PP_DAY = 864e5;
const ppQs = new URLSearchParams(location.search);
const ppQA = ppQs.get("pp_qa") === "1";
const ppStore = {
  get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} },
  del(k) { try { localStorage.removeItem(k); } catch (e) {} },
};
const ppTest = () => !!(window.LP && LP.test);
// PostHog events carry the trigger and the arm, never the name, the email or the design
const ppPh = (name, props) => { try { if (window.posthog && posthog.capture) posthog.capture(name, Object.assign({ lp_exp: window.LP && LP.exp, lp_arm: window.LP && LP.arm }, props)); } catch (e) {} };
const ppUuid = () => {
  if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16)); b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
  const x = Array.from(b, (n) => ("0" + n.toString(16)).slice(-2)).join("");
  return x.slice(0, 8) + "-" + x.slice(8, 12) + "-" + x.slice(12, 16) + "-" + x.slice(16, 20) + "-" + x.slice(20);
};
const ppSizeLabel = () => find(DATA.sizes, state.size).label;
const ppFrameLabel = () => (state.frame === "FRM-UNFRAMED" ? "Print only" : find(DATA.frames, state.frame).label + " frame");

function ppDesign() {
  return { skin: state.skin, hair: state.hair, hairColour: state.hairColour, kit: state.kit, background: state.background, paper: state.paper, name: state.name, number: state.number, size: state.size, frame: state.frame };
}
function ppSig() { return designParams().toString(); }

/* who this is: the bandit's identity keys (local id, Omnisend contact id from an email link or cookie, hashed Shopify visitor id) */
let ppIdentP = null;
function ppIdent() {
  if (ppIdentP) return ppIdentP;
  let l = ppStore.get("lp_lid"); if (!l) { l = ppUuid(); ppStore.set("lp_lid", l); }
  const ck = document.cookie.match(/(?:^|; )omnisendContactID=([^;]+)/);
  const out = { l, c: ppQs.get("omnisendContactID") || ppQs.get("c") || (ck ? decodeURIComponent(ck[1]) : "") };
  const ym = document.querySelector('meta[name="shopify-y"]');
  const hashed = ym && ym.content && window.crypto && crypto.subtle
    ? crypto.subtle.digest("SHA-256", new TextEncoder().encode(ym.content)).then((d) => { out.y = Array.from(new Uint8Array(d), (n) => ("0" + n.toString(16)).slice(-2)).join(""); }, () => {})
    : Promise.resolve();
  ppIdentP = hashed.then(() => out);
  return ppIdentP;
}
function ppSource() {
  const s = {};
  for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) if (ppQs.get(k)) s[k] = ppQs.get(k).slice(0, 80);
  if (ppQs.get("fbclid")) s.fbclid = 1; if (ppQs.get("gclid")) s.gclid = 1;
  if (window.LP && LP.src) s.src = LP.src;
  return s;
}
function ppPost(path, body, keepalive, ms) {
  const ctl = typeof AbortController === "function" ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), ms || 15000) : 0;
  return fetch(PP_HUB + path, { method: "POST", headers: { "content-type": "text/plain" }, body: JSON.stringify(body), keepalive: !!keepalive, credentials: "omit", signal: ctl ? ctl.signal : undefined })
    .then((r) => r.json()).finally(() => clearTimeout(timer));
}
// the render for the current design, only if the preview service has already made it (never waits)
function ppPreviewNow() {
  const job = cdnJobs.get(pictureKey());
  return job ? Promise.race([job, Promise.resolve(null)]) : Promise.resolve(null);
}

/* ---------- saving ---------- */

let ppHash = null, ppBase = null, ppChangedAt = 0, ppSaveTimer = 0, ppAddedThisView = false;
/* Add to basket pressed: from the click itself, not from the end of the add (that takes one to three seconds: the picture wait, /cart/add.js,
   the basket attribute). 14 Oct 2026: an exit survey could be called in that gap by the mouse leaving through the top of the window. Cleared only if the add fails. */
let ppAddClickedAt = 0;
function ppAddBusy() { return ppAddedThisView || ppAddClickedAt > 0; }
/* The exit survey asks people who really gave the designer a go (15 Oct 2026: 12 showings, 0 answers, 5 within 7 to 19 seconds of the visit starting,
   4 followed by an instant reopen). It needs the designer open for 30 seconds, three settled design changes and no add click, and after a close it
   waits about 6 seconds and is dropped if they come back. */
var ppOpenedAt = 0, ppOpenSeq = 0, ppChanges = 0, ppLastSig = null, ppRecStarted = false, ppSurveyTimer = 0, ppSurveyCalled = false;
const PP_SURVEY_MIN_MS = 30000, PP_SURVEY_MIN_CHANGES = 3, PP_SURVEY_CLOSE_WAIT = 6000;
function ppSurveyReady() {
  return !ppAddBusy() && ppOpenedAt > 0 && Date.now() - ppOpenedAt >= (ppQA ? 3000 : PP_SURVEY_MIN_MS) && ppChanges >= (ppQA ? 1 : PP_SURVEY_MIN_CHANGES);
}
/* Called each time the designer opens. The first open of a page view also starts PostHog's session recorder, which is otherwise left out for
   nine visitors in ten so that the page loads fast; it records from that moment on, and only once. */
function ppDesignerOpened() {
  ppOpenSeq++;
  if (!ppOpenedAt) ppOpenedAt = Date.now();
  if (ppRecStarted) return;
  ppRecStarted = true;
  try {
    const ph = window.posthog;
    if (ph && typeof ph.startSessionRecording === "function" && !(typeof ph.sessionRecordingStarted === "function" && ph.sessionRecordingStarted())) ph.startSessionRecording();
  } catch (e) {}
}
function ppGotHash(hash) {
  if (!hash || !/^[0-9a-zA-Z_-]{6,64}$/.test(hash)) return;
  ppHash = hash; window.PP_DESIGN_HASH = hash;
  try { const rec = JSON.parse(ppStore.get(PP_KEY.saved) || "null"); if (rec) { rec.h = hash; ppStore.set(PP_KEY.saved, JSON.stringify(rec)); } } catch (e) {}
  // pixel tab's hook (ViewContent with content_ids [hash]) listens for this; once per hash is handled there
  document.dispatchEvent(new CustomEvent("pp:design-saved", { detail: { hash } }));
}
function ppSettled() {
  clearTimeout(ppSaveTimer);
  ppSaveTimer = setTimeout(ppSave, 900);
}
async function ppSave() {
  const sig = ppSig();
  if (ppBase === null) { ppBase = sig; return; } // the design the page opened on: nothing to keep yet
  if (sig === ppBase) return;
  if (sig !== ppLastSig) { ppLastSig = sig; ppChanges++; }
  if (!ppChangedAt) ppChangedAt = Date.now();
  const d = ppDesign();
  ppStore.set(PP_KEY.saved, JSON.stringify({ d, h: ppHash, t: Date.now() }));
  ppStore.del(PP_KEY.fresh);
  try {
    const id = await ppIdent();
    const preview = await ppPreviewNow();
    const r = await ppPost("design", Object.assign({}, id, { vid: (window.LP && LP.vid) || "", src: (window.LP && LP.src) || "", test: ppTest(), design: d, stage: nearBuying ? "size_frame" : "started", preview_url: preview || "", source: ppSource() }), false);
    if (r && r.ok) ppGotHash(r.hash);
  } catch (e) {}
}
// a stage change without a design change: they reached size and frame
function ppReached() { if (ppBase !== null && ppSig() !== ppBase) ppSettled(); }

/* ---------- bringing them back ---------- */

const ppValid = (list, code) => Array.isArray(list) && list.some((o) => o.code === code);
function ppApply(d) {
  if (!d || typeof d !== "object") return false;
  if (ppValid(DATA.skinTones, d.skin)) state.skin = d.skin;
  if (ppValid(DATA.hairStyles, d.hair)) state.hair = d.hair;
  if (ppValid(DATA.hairColours, d.hairColour)) state.hairColour = d.hairColour;
  if (ppValid(DATA.kits, d.kit)) state.kit = d.kit;
  if (ppValid(DATA.backgrounds, d.background)) state.background = d.background;
  if (ppValid(DATA.sizes, d.size)) state.size = d.size;
  if (ppValid(DATA.frames, d.frame)) state.frame = d.frame;
  if (typeof d.name === "string" && normaliseName(d.name)) state.name = normaliseName(d.name);
  if (d.number != null) state.number = Math.max(0, Math.min(99, parseInt(d.number, 10) || 0));
  nameInput.value = state.name; numberInput.value = String(state.number);
  groupedTiles("pp-kit-tabs", "pp-kit", KIT_GROUPS, DATA.kits, "kit", kitSwatch);
  groupedTiles("pp-background-tabs", "pp-background", BG_GROUPS, DATA.backgrounds, "background", backgroundSwatch);
  ppBase = ppSig(); // what was restored is the starting point: only a change from here is saved again
  refresh();
  return true;
}
function ppNewDesign() {
  ppStore.del(PP_KEY.saved); ppStore.set(PP_KEY.fresh, String(Date.now()));
  const u = new URL(location.href); u.searchParams.delete("r"); u.searchParams.delete("cart");
  location.assign(u.toString());
}
let ppWelcomeShown = false;
function ppWelcome() {
  if (ppWelcomeShown) return; ppWelcomeShown = true;
  const make = (cls, tag) => { const el = document.createElement(tag || "p"); el.className = cls; el.appendChild(document.createTextNode("Welcome back: here's ")); const b = document.createElement("b"); b.textContent = state.name; el.appendChild(b); el.appendChild(document.createTextNode("'s design. ")); const a = document.createElement("button"); a.type = "button"; a.className = "pp-welcome-new"; a.textContent = "Start a new design"; a.addEventListener("click", ppNewDesign); el.appendChild(a); return el; };
  // inside the designer (no layout shift: it is in the sheet) and a short note over the page
  const line = make("pp-welcome-line"); line.setAttribute("role", "status");
  const designerEl = $("pp-designer"); if (designerEl) designerEl.insertBefore(line, designerEl.firstChild);
  const pill = make("pp-welcome", "div"); pill.setAttribute("role", "status");
  document.body.appendChild(pill);
  setTimeout(() => { pill.classList.add("is-out"); setTimeout(() => pill.remove(), 400); }, 15000);
}
function ppRestored(rec, source) {
  if (ppBase !== null && ppSig() !== ppBase && ppChangedAt) return false; // they have already started changing things
  if (!ppApply(rec.design || rec.d)) return false;
  if (rec.hash || rec.h) ppGotHash(rec.hash || rec.h);
  ppWelcome();
  ppPh("pp_design_restored", { source });
  return true;
}
function ppGetHub(query, ms) {
  const ctl = typeof AbortController === "function" ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), ms) : 0;
  return fetch(PP_HUB + "design?" + query, { credentials: "omit", signal: ctl ? ctl.signal : undefined })
    .then((r) => (r.ok ? r.json() : null)).catch(() => null).finally(() => clearTimeout(timer));
}
function ppRestoreInit() {
  const fresh = Number(ppStore.get(PP_KEY.fresh)) || 0;
  let local = null; try { local = JSON.parse(ppStore.get(PP_KEY.saved) || "null"); } catch (e) {}
  const localOk = !!(local && local.d && local.t > fresh && Date.now() - local.t < 30 * PP_DAY);
  const r = ppQs.get("r");
  const openAtSize = () => {
    if (ppQs.get("cart") !== "1") return;
    if (document.getElementById("pp-b-customise") && typeof openB === "function") openB("restore"); else if (root.dataset.mode === "customise") openSheet();
    setTimeout(() => { const p = $("pp-size-panel"); if (p && p.scrollIntoView) p.scrollIntoView({ block: "start" }); }, 450);
  };
  const viaHubIdentity = () => {
    if (localOk) return;
    ppIdent().then((id) => ppGetHub("l=" + encodeURIComponent(id.l) + (id.c ? "&c=" + encodeURIComponent(id.c) : "") + (id.y ? "&y=" + id.y : "") + (ppTest() ? "&t=1" : ""), 800)).then((res) => {
      if (!res || !res.ok) return;
      // a design the person started over after is not brought back
      if (fresh && Date.now() - Number(res.age_days) * PP_DAY <= fresh) return;
      ppRestored(res, "hub");
    });
  };
  if (r && /^[0-9a-zA-Z_-]{6,64}$/.test(r)) {
    ppGetHub("h=" + encodeURIComponent(r), 3500).then((res) => {
      if (res && res.ok && ppRestored(res, "link")) { openAtSize(); return; }
      // an expired or mistyped link: fall back to whatever this browser or the hub holds
      if (localOk && ppRestored(local, "local")) return;
      viaHubIdentity();
    });
    return;
  }
  if (localOk) { ppRestored(local, "local"); return; }
  viaHubIdentity();
}

/* ---------- "Email me this design" and the smart prompt ---------- */

const cap = { el: null, shown: false, trigger: "link" };
function ppEsc(e) { if (e.key === "Escape" && cap.shown) { e.stopPropagation(); ppCapHide(); } }
function ppCapBuild() {
  if (cap.el) return cap.el;
  const el = document.createElement("div");
  el.className = "pp-cap"; el.hidden = true; el.setAttribute("role", "dialog"); el.setAttribute("aria-labelledby", "pp-cap-title");
  el.innerHTML = '<form class="pp-cap-card" novalidate>'
    + '<button type="button" class="pp-cap-x" aria-label="Close">&times;</button>'
    + '<div class="pp-cap-main"><div class="pp-cap-pic" aria-hidden="true"><img alt="" decoding="async"></div>'
    + '<div class="pp-cap-text"><p class="pp-cap-title" id="pp-cap-title"></p><p class="pp-cap-sub"></p></div></div>'
    + '<div class="pp-cap-fields">'
    + '<label class="pp-cap-label" for="pp-cap-email">Your email</label>'
    + '<input class="pp-cap-email ph-no-capture" id="pp-cap-email" type="email" inputmode="email" autocomplete="email" autocapitalize="none" spellcheck="false" placeholder="name@example.com">'
    + '<input class="pp-cap-hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">'
    + '<p class="pp-cap-consent">We\'ll email your design and the odd update. Unsubscribe any time.</p>'
    + '<label class="pp-cap-opt"><input type="checkbox" id="pp-cap-optout"> <span>Just the design, please</span></label>'
    + '<p class="pp-cap-error" role="alert" hidden></p>'
    + '<button type="submit" class="pp-cap-send">Email it to me</button></div>'
    + '<div class="pp-cap-done" hidden><p class="pp-cap-sent">Sent: check your inbox.</p><button type="button" class="pp-link pp-cap-close2">Close</button></div>'
    + '</form>';
  document.body.appendChild(el);
  const form = el.querySelector("form");
  el.querySelector(".pp-cap-x").addEventListener("click", ppCapHide);
  el.querySelector(".pp-cap-close2").addEventListener("click", ppCapHide);
  el.addEventListener("keydown", (e) => { e.stopPropagation(); ppEsc(e); }); // the sheet's own key handling (focus trap) stays out of this panel
  form.addEventListener("submit", (e) => { e.preventDefault(); ppCapSend(); });
  cap.el = el;
  return el;
}
// a tap anywhere outside the card closes it, and is not swallowed
function ppOutside(e) { if (cap.shown && cap.el && !cap.el.contains(e.target) && !e.target.closest("#pp-email-link")) ppCapHide(); }
function ppCapShow(trigger) {
  if (cap.shown) return false;
  const el = ppCapBuild();
  cap.shown = true; cap.trigger = trigger;
  const name = state.name;
  el.querySelector(".pp-cap-title").textContent = trigger === "link" ? "Email me this design" : "Save " + name + "'s design?";
  el.querySelector(".pp-cap-sub").textContent = trigger === "link"
    ? "We'll send " + name + "'s design to your inbox so you can finish later or check the details with the family."
    : "We'll email it so you can finish later or check the details with the family.";
  el.querySelector(".pp-cap-fields").hidden = false; el.querySelector(".pp-cap-done").hidden = true;
  el.querySelector(".pp-cap-error").hidden = true;
  const img = el.querySelector(".pp-cap-pic img"); img.removeAttribute("src"); el.querySelector(".pp-cap-pic").classList.remove("is-ready");
  const key = pictureKey();
  cdnFor(key).then((url) => { if (url && key === pictureKey() && cap.shown) { img.src = url; img.onload = () => el.querySelector(".pp-cap-pic").classList.add("is-ready"); } });
  // The email uses the large (1080 px) picture, which the hub asks the same worker for and which takes 15 seconds or more on a design never drawn before. Start it now, while
  // they type their email, so it is ready (and remembered by the worker) by the time they press Send and the hub's answer takes a second or two, not fifteen (16 Oct 2026).
  cdnFor(key + "&w=1080&fmt=jpg");
  el.hidden = false;
  document.addEventListener("pointerdown", ppOutside, true);
  document.addEventListener("keydown", ppEsc, true);
  ppPh("pp_capture_shown", { trigger });
  if (trigger !== "link") { ppStore.set(PP_KEY.prompt, String(Date.now())); }
  if (trigger === "link") setTimeout(() => { try { el.querySelector(".pp-cap-email").focus({ preventScroll: true }); } catch (e) {} }, 60);
  return true;
}
function ppCapHide() {
  if (!cap.shown) return;
  cap.shown = false; cap.el.hidden = true;
  document.removeEventListener("pointerdown", ppOutside, true);
  document.removeEventListener("keydown", ppEsc, true);
}
async function ppCapSend() {
  const el = cap.el, input = el.querySelector(".pp-cap-email"), err = el.querySelector(".pp-cap-error"), btn = el.querySelector(".pp-cap-send");
  const email = input.value.trim();
  if (!/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email)) { err.textContent = "Please check your email address."; err.hidden = false; input.focus(); return; }
  err.hidden = true; btn.disabled = true; btn.textContent = "Sending…";
  try {
    const id = await ppIdent();
    const variant = variantFor(state.size, state.frame);
    // The small picture (for the basket line the hub keeps), if the page has it: it was already asked for when the panel opened, so wait only a moment. The large one for the
    // email is made by the hub through the same worker, and was started when the panel opened (see ppCapShow). If that is still being made, the hub waits up to 12 seconds, then
    // sends the email with the worker's own link to it rather than failing (hub change, 16 Oct 2026).
    const preview = await Promise.race([cdnFor(pictureKey()), new Promise((r) => setTimeout(() => r(null), 4000))]);
    const payload = Object.assign({}, id, {
      vid: (window.LP && LP.vid) || "", src: (window.LP && LP.src) || "", test: ppTest(), email, capture_source: cap.trigger,
      optout: el.querySelector("#pp-cap-optout").checked, website: el.querySelector(".pp-cap-hp").value,
      design: ppDesign(), stage: nearBuying ? "size_frame" : "started", preview_url: preview || "", source: ppSource(),
      price: variant ? Number(variant.price) / 100 : 0, size_label: ppSizeLabel(), frame_label: ppFrameLabel(),
    });
    // one automatic second try when the hub could not make the picture or reach the email service (what people did by hand, and it worked); a wrong email or the limit is not retried
    let r = null;
    for (let i = 0; i < 2 && !(r && r.ok); i++) {
      if (i) await new Promise((res) => setTimeout(res, 2500));
      try { r = await ppPost("capture", payload, false, 45000); } catch (x) { r = null; }
      if (r && r.ok === false && !["render", "omnisend", "save"].includes(r.error)) break;
    }
    if (!r || !r.ok) throw new Error("capture");
    ppStore.set(PP_KEY.captured, String(Date.now()));
    if (r.hash) ppGotHash(r.hash);
    ppPh("pp_capture_submitted", { trigger: cap.trigger, optout: el.querySelector("#pp-cap-optout").checked });
    el.querySelector(".pp-cap-fields").hidden = true; el.querySelector(".pp-cap-done").hidden = false;
    el.querySelector(".pp-cap-sent").setAttribute("tabindex", "-1"); el.querySelector(".pp-cap-sent").focus({ preventScroll: true });
  } catch (e) {
    err.textContent = "Sorry, that didn't send. Please try again."; err.hidden = false;
  }
  btn.disabled = false; btn.textContent = "Email it to me";
}

/* when the prompt may show: at most once in 3 days; only after they reached size and frame or designed for 45 seconds; never
   after an add to basket or once they have asked for the email; and only after they changed something. */
function ppPromptOk() {
  if (cap.shown || ppAddBusy()) return false;
  if (ppStore.get(PP_KEY.added) || ppStore.get(PP_KEY.captured)) return false;
  if (!ppQA && Date.now() - (Number(ppStore.get(PP_KEY.prompt)) || 0) < 3 * PP_DAY) return false;
  if (!ppChangedAt) return false;
  return nearBuying || Date.now() - ppChangedAt >= (ppQA ? 3000 : 45000);
}
// the designer sheet closed without an add: the save prompt, or if it is not due, the exit survey's event
function ppOnClose() {
  if (ppAddBusy()) return;
  if (ppPromptOk()) { setTimeout(() => ppCapShow("close"), 260); return; }
  if (!ppSurveyReady()) return;
  ppExitSurvey(false, true);
}
// The exit survey is an API survey in PostHog: it appears only here, about a second after the person really closed the designer
// (X, scrim, Escape, back or swipe) without an add and without the save prompt. It is not tied to any event, so it can never show
// late, on coming back to the tab, or on scrolling (13 Oct: a trigger event fired earlier showed it seven minutes later).
const PP_EXIT_SURVEY = "01a134ed-af4e-0000-3bcd-f2d8d64501f1";
function ppExitSurvey(fromExitIntent, afterClose) {
  if (ppAddBusy() || ppSurveyCalled) return;
  const seq = ppOpenSeq;
  clearTimeout(ppSurveyTimer);
  ppSurveyTimer = setTimeout(() => {
    try {
      if (ppAddBusy() || cap.shown || document.visibilityState !== "visible" || ppSurveyCalled) return;
      // after a close: they came back to the designer (even for a moment), so this was not a leaving
      if (afterClose && (bOpen() || sheet.classList.contains("is-open") || ppOpenSeq !== seq)) return;
      const ph = window.posthog;
      // conditions stay on (once per person, the page, the event just sent)
      if (ph && typeof ph.displaySurvey === "function") {
        // exit intent is once per person: only now that the survey really is being shown
        if (fromExitIntent) { try { localStorage.setItem("pp_xi", String(Date.now())); } catch (x) {} }
        ppSurveyCalled = true;
        // the event the survey is conditioned on, sent only when the survey really is called (a cancelled one leaves no trace)
        ppPh("pp_exit_feedback", fromExitIntent ? { trigger: "exit_intent" } : {});
        ph.displaySurvey(PP_EXIT_SURVEY, { displayType: "popover", ignoreConditions: false, ignoreDelay: true }); ppFixSurveysSoon(); }
    } catch (e) {}
  }, afterClose ? PP_SURVEY_CLOSE_WAIT : 1000);
}
// PostHog draws its surveys inside a shadow root that the page's CSS cannot reach. In iPhone Safari (WebKit) the answer list was wider than the
// survey box (13 Oct: a fieldset keeps its content's minimum width, and the "Something else" text field is wide), so the answers ran off the right
// side. A small style is added inside each survey's shadow root as it appears.
const PP_SURVEY_FIX = ".ph-survey fieldset, .ph-survey .multiple-choice-options, .ph-survey label, .ph-survey .choice-option-open { min-width: 0 !important; max-width: 100% !important; } "
  + ".ph-survey input[type=text], .ph-survey textarea { min-width: 0 !important; width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }";
function ppFixSurveys() {
  for (const host of document.querySelectorAll('[class*="PostHogSurvey-"]')) {
    const root = host.shadowRoot;
    if (root && !root.querySelector("style[data-pp-fix]")) { const st = document.createElement("style"); st.setAttribute("data-pp-fix", ""); st.textContent = PP_SURVEY_FIX; root.appendChild(st); }
  }
}
// the survey's box is built a moment after its host appears, so keep checking for a few seconds after anything that can show one
let ppFixTimer = 0;
function ppFixSurveysSoon() {
  clearInterval(ppFixTimer); ppFixSurveys();
  let n = 0; ppFixTimer = setInterval(() => { ppFixSurveys(); if (++n > 60) clearInterval(ppFixTimer); }, 100);
}
/* The request box ("Can't find your team, kit or hairstyle?", under the kit options and the hair options). It replaces two links that opened a PostHog
   widget survey and failed for most people (16 Oct 2026: 83 taps, the survey shown 5 times). The box is always on the page and sends its answer to PostHog as the
   same "survey sent" event the survey made, so answers keep landing in that survey's results. */
const PP_REQ_SURVEY = "01a134ee-00c0-0000-ca44-68c05743fa5d", PP_REQ_Q1 = "06eb7fd3-cd6a-499e-8faa-6a5f085bfb25", PP_REQ_Q2 = "23abd928-9c82-4f2c-bed7-af2cc0f082c0";
let ppReqSent = 0;
function ppRequestBoxes() {
  for (const box of document.querySelectorAll(".pp-req")) {
    const toggle = box.querySelector(".pp-req-toggle"), body = box.querySelector(".pp-req-body"), form = box.querySelector(".pp-req-form");
    toggle.addEventListener("click", () => {
      const open = toggle.getAttribute("aria-expanded") !== "true";
      toggle.setAttribute("aria-expanded", open ? "true" : "false"); body.hidden = !open;
      if (open) ppPh("pp_request_open", { section: box.dataset.section });
    });
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const text = box.querySelector(".pp-req-text").value.trim(), email = box.querySelector(".pp-req-email").value.trim();
      const err = box.querySelector(".pp-req-error"), send = box.querySelector(".pp-req-send");
      err.hidden = true;
      if (text.length < 2) { err.textContent = "Please tell us what you can't find."; err.hidden = false; box.querySelector(".pp-req-text").focus(); return; }
      if (email && !/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email)) { err.textContent = "Please check your email address, or leave it empty."; err.hidden = false; box.querySelector(".pp-req-email").focus(); return; }
      if (ppReqSent >= 5) { err.textContent = "Thanks, we have plenty from you for now."; err.hidden = false; return; }
      const ph = window.posthog;
      if (!ph || typeof ph.capture !== "function") { err.textContent = "Sorry, that didn't send. Please email hello@footballwallcharts.com instead."; err.hidden = false; return; }
      send.disabled = true;
      try {
        const q1 = { id: PP_REQ_Q1, question: "Which team, kit, hairstyle or colour can't you find?", response: text.slice(0, 200) };
        const questions = [q1];
        const props = { $survey_id: PP_REQ_SURVEY, $survey_name: "Can't see your team, kit or style?", $survey_completed: true, $survey_submission_id: ppUuid(), $survey_questions: questions };
        props["$survey_response_" + PP_REQ_Q1] = q1.response;
        if (email) { const q2 = { id: PP_REQ_Q2, question: "Want us to tell you when it's added? Leave your email (optional)", response: email }; questions.push(q2); props["$survey_response_" + PP_REQ_Q2] = email; }
        ph.capture("survey sent", props);
        ppPh("pp_request_sent", { section: box.dataset.section, has_email: !!email });
        ppReqSent++;
        box.querySelector(".pp-req-form").hidden = true; box.querySelector(".pp-req-thanks").hidden = false;
      } catch (x) { err.textContent = "Sorry, that didn't send. Please try again."; err.hidden = false; send.disabled = false; }
    });
  }
}
ppRequestBoxes();
try {
  new MutationObserver(ppFixSurveys).observe(document.documentElement, { childList: true, subtree: true });
  ppFixSurveys();
} catch (e) {}
// Desktop exit intent (Philip said yes, 13 Oct; set PP_EXIT_INTENT to false to turn it off).
// The mouse leaves through the top of the window after they changed the design and have not added; once per person; never on a touch screen.
const PP_EXIT_INTENT = true;
(function ppExitIntent() {
  if (!PP_EXIT_INTENT) return;
  if (!window.matchMedia || !matchMedia("(hover: hover) and (pointer: fine)").matches) return;
  document.documentElement.addEventListener("mouseleave", (e) => {
    if (e.clientY > 0) return; // only out through the top of the window (towards the tabs and the address bar)
    if (!ppSurveyReady() || cap.shown) return;
    try { if (localStorage.getItem("pp_xi")) return; } catch (x) {}
    ppExitSurvey(true, false);
  });
})();
// 30 seconds with nothing touched, at size and frame, with the designer open
let ppIdleTimer = 0;
function ppDesignerOpen() { return bOpen() || sheet.classList.contains("is-open") || root.dataset.mode === "inline"; }
function ppAnyOverlay() {
  return document.body.classList.contains("upcartPopupShow") || $("pp-guide").classList.contains("is-open") || $("pp-big").classList.contains("is-open")
    || (document.getElementById("pp-wall-box") && !document.getElementById("pp-wall-box").hidden) || Array.from(document.querySelectorAll("dialog[open]")).some((d) => !root.contains(d));
}
function ppIdleReset() {
  clearTimeout(ppIdleTimer);
  if (cap.shown) return;
  // started from any touch; nearBuying is checked when the wait is over (it can be set a moment after the observer below fires)
  ppIdleTimer = setTimeout(() => {
    if (nearBuying && document.visibilityState === "visible" && ppDesignerOpen() && !ppAnyOverlay() && ppPromptOk()) ppCapShow("idle");
    else ppIdleReset();
  }, ppQA ? 5000 : 30000);
}
for (const ev of ["pointerdown", "keydown", "input", "scroll", "touchstart", "wheel"]) document.addEventListener(ev, ppIdleReset, { capture: true, passive: true });
if ($("pp-email-link")) $("pp-email-link").addEventListener("click", () => { if (cap.shown) { ppCapHide(); return; } ppCapShow("link"); });

/* ---------- after an add to basket ---------- */

// kept so the prompt never shows again, and the hub tells Omnisend (a known contact only; test visits are skipped there) so the
// basket email can show their own design. Fire and forget: the basket never waits for this.
async function ppAfterAdd(variant) {
  ppAddedThisView = true; ppStore.set(PP_KEY.added, String(Date.now()));
  ppCapHide();
  try {
    const id = await ppIdent();
    let cart = {}; try { cart = await (await fetch("/cart.js", { headers: { "Accept": "application/json" } })).json(); } catch (e) {}
    const preview = await ppPreviewNow();
    const r = await ppPost("cart", Object.assign({}, id, {
      vid: (window.LP && LP.vid) || "", src: (window.LP && LP.src) || "", test: ppTest(), design: ppDesign(), preview_url: preview || "", source: ppSource(),
      variant_id: String(variant.id), price: Number(variant.price) / 100, cart_total: Number(cart.total_price || variant.price) / 100, cart_token: cart.token || "", eid: ppUuid(),
    }), true);
    if (r && r.ok && r.hash) ppGotHash(r.hash);
  } catch (e) {}
}

// reaching size and frame: save the stage, and start watching for 30 quiet seconds
try {
  const reached = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting)) { reached.disconnect(); ppReached(); ppIdleReset(); }
  }, { rootMargin: "200px 0px" });
  for (const id of ["pp-size-panel", "pp-frame-panel", "pp-atc"]) if ($(id)) reached.observe($(id));
} catch (e) {}

ppRestoreInit();

/* The hair choices are each a small painting of that haircut. Working one out means reading its whole painting, so
   they are not done on load: the chosen one is drawn at once (its painting is already worked out for the poster),
   the others once the hair choices come near the screen, one at a time with a pause between, so the page never
   stops answering for long. A style chosen before its tile is drawn is simply worked out when chosen. */
function whenIdle(fn) { return window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 120); }
var hairTilesNear = false, hairTileRun = 0; // var: the first refresh() runs before this line
function queueHairTiles() {
  const host = $("pp-hair"); if (!host) return;
  const run = ++hairTileRun;
  const chosen = [...host.children].find((b) => b.dataset.code === state.hair);
  if (chosen) drawHairTile(chosen.querySelector("canvas"), figureFor(chosen.dataset.code));
  if (!hairTilesNear) return;
  const rest = [...host.children].filter((b) => b !== chosen);
  const next = () => {
    if (run !== hairTileRun || !rest.length) return;
    const btn = rest.shift();
    drawHairTile(btn.querySelector("canvas"), figureFor(btn.dataset.code)).then(() => whenIdle(next), () => whenIdle(next));
  };
  whenIdle(next);
}
try {
  const hairSeen = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting)) { hairTilesNear = true; hairSeen.disconnect(); queueHairTiles(); }
  }, { rootMargin: "300px 0px" });
  hairSeen.observe($("pp-hair"));
} catch (e) { hairTilesNear = true; queueHairTiles(); }

// Clear the theme's absolute header: measure it and expose the height as a CSS variable.
(function ppHeaderOffset() {
  const hero = document.querySelector('.pp-hero');
  if (!hero) return;
  const apply = () => {
    const h = document.querySelector('header-component, .header-section header, header.header');
    const px = h ? Math.round(h.getBoundingClientRect().height) : 0;
    if (px > 0) hero.style.setProperty('--pp-header-h', (px + 8) + 'px');
  };
  apply();
  window.addEventListener('resize', apply);
  setTimeout(apply, 300);
})();

/* ---------- "See it on their wall": the room photos with their own design in the frame ---------- */
// Each room photo (1200 x 1200) with the four corners of the paper inside its frame (top left, top right, bottom
// right, bottom left) and the paper's colour just inside each corner, measured from the photo, so the design takes
// on the room's own light. The frame and the room are never drawn over. The size chart is shown as it is.
const WALL_ROOMS = [
  { id: "02", frame: "FRM-BLACK", caption: "Shown in a black frame", quad: [[427.9, 173.6], [772.9, 173.6], [772.1, 633.9], [429.0, 634.3]], paper: [[211, 212, 216], [200, 196, 195], [242, 241, 247], [221, 221, 229]] },
  { id: "03", frame: "FRM-OAK", caption: "Shown in an oak frame", quad: [[603.9, 141.8], [960.4, 142.4], [963.5, 627.8], [604.9, 627.8]], paper: [[215, 209, 219], [200, 191, 195], [239, 225, 224], [214, 203, 201]] },
  { id: "04", frame: "FRM-WHITE", caption: "Shown in a white frame", quad: [[424.2, 110.7], [777.5, 110.7], [777.5, 611.1], [423.6, 611.1]], paper: [[253, 252, 254], [254, 254, 254], [254, 252, 243], [254, 253, 245]] },
  { id: "05", frame: "FRM-OAK", caption: "Shown in an oak frame", quad: [[384.4, 184.6], [816.8, 184.6], [822.7, 792.2], [380.9, 792.2]], paper: [[223, 223, 231], [208, 206, 208], [211, 207, 209], [220, 219, 225]] },
];
const WALL_CHART = { id: "01", caption: "Four sizes, shown to scale", chart: true };
const wallStrip = $("pp-wall-strip");
const wallPhotos = {};
var wallArt = null, wallArtVersion = 0, wallTimer = 0, wallOrderKey = ""; // var: the first refresh() runs before this line

function wallPhoto(id) {
  if (!wallPhotos[id]) {
    wallPhotos[id] = new Promise((resolve, reject) => {
      const img = new Image(); img.decoding = "async";
      img.onload = () => resolve(img); img.onerror = reject;
      img.src = (window.PP_ASSETS && PP_ASSETS.wall && PP_ASSETS.wall[id]) || "";
    });
  }
  return wallPhotos[id];
}
// the design as one picture: the painting, then the name and number, then the hair that falls over them
function bakeArt(width) {
  return new Promise((resolve) => {
    const c = $("pp-canvas");
    if (!c || !c.width) return resolve(null);
    const out = document.createElement("canvas");
    out.width = width; out.height = Math.round(width * c.height / c.width);
    const g = out.getContext("2d"); g.drawImage(c, 0, 0, out.width, out.height);
    const finish = () => { try { g.drawImage($("pp-hair-over"), 0, 0, out.width, out.height); } catch (e) {} resolve(out); };
    const img = new Image();
    img.onload = () => { g.drawImage(img, 0, 0, out.width, out.height); finish(); };
    img.onerror = finish;
    letteringSrc(c.width, c.height).then((src) => { img.src = src; });
  });
}
// one source triangle onto one destination triangle (an affine map), clipped, slightly grown so no seams show
function drawTriangle(g, img, s0, s1, s2, d0, d1, d2) {
  // each clip reaches 1.5 px past its corners so neighbouring triangles overlap and no hairline seam shows
  const cx = (d0[0] + d1[0] + d2[0]) / 3, cy = (d0[1] + d1[1] + d2[1]) / 3;
  const grow = (p) => { const dx = p[0] - cx, dy = p[1] - cy, l = Math.hypot(dx, dy) || 1; return [p[0] + dx / l * 1.5, p[1] + dy / l * 1.5]; };
  const [e0, e1, e2] = [grow(d0), grow(d1), grow(d2)];
  g.save(); g.beginPath(); g.moveTo(e0[0], e0[1]); g.lineTo(e1[0], e1[1]); g.lineTo(e2[0], e2[1]); g.closePath(); g.clip();
  const den = s0[0] * (s1[1] - s2[1]) + s1[0] * (s2[1] - s0[1]) + s2[0] * (s0[1] - s1[1]);
  if (!den) { g.restore(); return; }
  const a = (d0[0] * (s1[1] - s2[1]) + d1[0] * (s2[1] - s0[1]) + d2[0] * (s0[1] - s1[1])) / den;
  const b = (d0[1] * (s1[1] - s2[1]) + d1[1] * (s2[1] - s0[1]) + d2[1] * (s0[1] - s1[1])) / den;
  const c = (d0[0] * (s2[0] - s1[0]) + d1[0] * (s0[0] - s2[0]) + d2[0] * (s1[0] - s0[0])) / den;
  const d = (d0[1] * (s2[0] - s1[0]) + d1[1] * (s0[0] - s2[0]) + d2[1] * (s1[0] - s0[0])) / den;
  const e = (d0[0] * (s1[0] * s2[1] - s2[0] * s1[1]) + d1[0] * (s2[0] * s0[1] - s0[0] * s2[1]) + d2[0] * (s0[0] * s1[1] - s1[0] * s0[1])) / den;
  const f = (d0[1] * (s1[0] * s2[1] - s2[0] * s1[1]) + d1[1] * (s2[0] * s0[1] - s0[0] * s2[1]) + d2[1] * (s0[0] * s1[1] - s1[0] * s0[1])) / den;
  g.transform(a, b, c, d, e, f); g.drawImage(img, 0, 0); g.restore();
}
// the design into the frame's four corners: a grid of small triangles, placed by the corners (close enough for these photos)
function warpInto(g, art, q) {
  const N = 10, W = art.width, H = art.height;
  const at = (u, v) => [
    (1 - u) * (1 - v) * q[0][0] + u * (1 - v) * q[1][0] + u * v * q[2][0] + (1 - u) * v * q[3][0],
    (1 - u) * (1 - v) * q[0][1] + u * (1 - v) * q[1][1] + u * v * q[2][1] + (1 - u) * v * q[3][1],
  ];
  // underneath, the whole design placed by three corners in one piece, so any gap left between triangles shows the design, not the wall
  g.save(); g.transform((q[1][0] - q[0][0]) / W, (q[1][1] - q[0][1]) / W, (q[3][0] - q[0][0]) / H, (q[3][1] - q[0][1]) / H, q[0][0], q[0][1]);
  g.drawImage(art, 0, 0); g.restore();
  for (let i = 0; i < N; i++) for (let j = 0; j < N; j++) {
    const u0 = i / N, u1 = (i + 1) / N, v0 = j / N, v1 = (j + 1) / N;
    const s00 = [u0 * W, v0 * H], s10 = [u1 * W, v0 * H], s11 = [u1 * W, v1 * H], s01 = [u0 * W, v1 * H];
    const d00 = at(u0, v0), d10 = at(u1, v0), d11 = at(u1, v1), d01 = at(u0, v1);
    drawTriangle(g, art, s00, s10, s11, d00, d10, d11);
    drawTriangle(g, art, s00, s11, s01, d00, d11, d01);
  }
}
const quadPath = (g, q) => { g.beginPath(); g.moveTo(q[0][0], q[0][1]); for (let k = 1; k < 4; k++) g.lineTo(q[k][0], q[k][1]); g.closePath(); };
function renderRoom(canvas, room, photo, art) {
  const S = canvas.width, k = S / 1200, q = room.quad.map((p) => [p[0] * k, p[1] * k]);
  const g = canvas.getContext("2d");
  g.drawImage(photo, 0, 0, S, S);
  if (!art) return;
  g.save(); quadPath(g, q); g.clip();
  warpInto(g, art, q);
  // the room's light: multiply by the paper's own colour at each corner, blended across (a 2 x 2 image, smoothed)
  const tint = document.createElement("canvas"); tint.width = 2; tint.height = 2;
  const tg = tint.getContext("2d"), px = tg.createImageData(2, 2), order = [0, 1, 3, 2];
  order.forEach((corner, n) => { const c = room.paper[corner]; px.data.set([c[0], c[1], c[2], 255], n * 4); });
  tg.putImageData(px, 0, 0);
  const xs = q.map((p) => p[0]), ys = q.map((p) => p[1]), x0 = Math.min(...xs), y0 = Math.min(...ys);
  g.globalCompositeOperation = "multiply"; g.imageSmoothingEnabled = true;
  g.drawImage(tint, 0.5, 0.5, 1, 1, x0, y0, Math.max(...xs) - x0, Math.max(...ys) - y0);
  // the frame's soft shadow falling onto the paper
  g.globalCompositeOperation = "source-over";
  g.shadowColor = "rgba(0,0,0,0.35)"; g.shadowBlur = Math.max(2, (Math.max(...xs) - x0) * 0.035); g.shadowOffsetX = 0; g.shadowOffsetY = Math.max(1, S * 0.002);
  g.beginPath(); g.rect(-S, -S, 3 * S, 3 * S); g.moveTo(q[0][0], q[0][1]); for (let n = 3; n >= 0; n--) g.lineTo(q[n][0], q[n][1]); g.closePath();
  g.fillStyle = "#000"; g.fill("evenodd");
  g.restore();
}
// the rooms in the chosen frame come first; print only keeps the usual order; the size chart is last
function wallOrder() {
  const rooms = WALL_ROOMS.slice().sort((a, b) => (b.frame === state.frame) - (a.frame === state.frame));
  return rooms.concat([WALL_CHART]);
}
function paintCard(card) {
  const item = card.__item, canvas = card.querySelector("canvas");
  if (!item || item.chart || !canvas || !wallArt || card.__drawn === wallArtVersion) return;
  const w = Math.max(1, Math.round(card.clientWidth * Math.min(2, window.devicePixelRatio || 1)));
  wallPhoto(item.id).then((photo) => {
    try {
      if (canvas.width !== w) { canvas.width = w; canvas.height = w; }
      renderRoom(canvas, item, photo, wallArt);
      card.__drawn = wallArtVersion; card.classList.add("is-live");
    } catch (e) { card.classList.remove("is-live"); }
  }, () => {});
}
// a card's photo is only fetched once the card comes near the screen (the browser's own lazy loading starts far too early)
const showPhoto = (card) => { const img = card.querySelector("img[data-src]"); if (img) { img.src = img.dataset.src; img.removeAttribute("data-src"); } };
const wallSeen = "IntersectionObserver" in window ? new IntersectionObserver((entries) => {
  for (const en of entries) { en.target.__visible = en.isIntersecting; if (en.isIntersecting) { showPhoto(en.target); paintCard(en.target); } }
}, { rootMargin: "200px" }) : null;
function buildWall() {
  if (!wallStrip) return;
  const items = wallOrder(), key = items.map((i) => i.id).join(",");
  if (key === wallOrderKey) return;
  wallOrderKey = key;
  const existing = {}; wallStrip.querySelectorAll(".pp-wall__card").forEach((c) => { existing[c.__item.id] = c; });
  items.forEach((item) => {
    let card = existing[item.id];
    if (!card) {
      card = document.createElement("button"); card.type = "button"; card.className = "pp-wall__card" + (item.chart ? " is-chart" : ""); card.setAttribute("role", "listitem");
      card.setAttribute("aria-label", item.chart ? item.caption : "Their design " + item.caption.toLowerCase() + ": open larger");
      const src = (window.PP_ASSETS && PP_ASSETS.wall && PP_ASSETS.wall[item.id]) || "";
      card.innerHTML = '<span class="pp-wall__pic"><img data-src="' + src + '" alt="" decoding="async" width="600" height="600">' + (item.chart ? "" : "<canvas></canvas>") + '</span><span class="pp-wall__cap">' + item.caption + "</span>";
      card.__item = item;
      if (!wallSeen) showPhoto(card);
      card.addEventListener("click", () => openWallBox(item.id));
      if (wallSeen) wallSeen.observe(card);
    }
    wallStrip.appendChild(card);
  });
}
function queueWall() {
  clearTimeout(wallTimer);
  wallTimer = setTimeout(() => {
    buildWall();
    bakeArt(540).then((art) => {
      if (!art) return;
      wallArt = art; wallArtVersion++;
      wallStrip.querySelectorAll(".pp-wall__card").forEach((c) => { if (!wallSeen || c.__visible) paintCard(c); });
      if (!$("pp-wall-box").hidden) paintBox();
      paintViews();
    });
  }, 300);
}
// the lightbox: one photo large, arrows, keys and a swipe
var boxIndex = 0;
function boxItems() { return wallOrder(); }
function paintBox() {
  const items = boxItems(), item = items[boxIndex], pic = $("pp-wall-pic");
  $("pp-wall-cap").textContent = item.caption;
  const size = Math.round(Math.min(window.innerWidth * 0.92, window.innerHeight * 0.8, 1100));
  pic.style.width = size + "px"; pic.style.height = size + "px";
  const src = (window.PP_ASSETS && PP_ASSETS.wall && PP_ASSETS.wall[item.id]) || "";
  pic.innerHTML = '<img src="' + src + '" alt="' + item.caption + '" width="' + size + '" height="' + size + '">';
  if (item.chart || !wallArt) return;
  const canvas = document.createElement("canvas"), w = Math.round(size * Math.min(2, window.devicePixelRatio || 1));
  canvas.width = w; canvas.height = w; canvas.setAttribute("role", "img"); canvas.setAttribute("aria-label", "Their design " + item.caption.toLowerCase());
  bakeArt(1000).then((art) => wallPhoto(item.id).then((photo) => { try { renderRoom(canvas, item, photo, art || wallArt); pic.appendChild(canvas); } catch (e) {} }));
}
function openWallBox(id) {
  boxIndex = Math.max(0, boxItems().findIndex((i) => i.id === id));
  $("pp-wall-box").hidden = false; document.documentElement.classList.add("pp-wall-open");
  paintBox(); $("pp-wall-close").focus({ preventScroll: true });
}
function closeWallBox() { $("pp-wall-box").hidden = true; document.documentElement.classList.remove("pp-wall-open"); }
function stepBox(n) { const len = boxItems().length; boxIndex = (boxIndex + n + len) % len; paintBox(); }
if (wallStrip) {
  $("pp-wall-close").addEventListener("click", closeWallBox);
  $("pp-wall-prev").addEventListener("click", () => stepBox(-1));
  $("pp-wall-next").addEventListener("click", () => stepBox(1));
  $("pp-wall-box").addEventListener("click", (e) => { if (e.target === $("pp-wall-box")) closeWallBox(); });
  document.addEventListener("keydown", (e) => {
    if ($("pp-wall-box").hidden) return;
    if (e.key === "Escape") closeWallBox(); else if (e.key === "ArrowLeft") stepBox(-1); else if (e.key === "ArrowRight") stepBox(1);
  });
  let touchX = null;
  $("pp-wall-box").addEventListener("touchstart", (e) => { touchX = e.touches[0].clientX; }, { passive: true });
  $("pp-wall-box").addEventListener("touchend", (e) => { if (touchX == null) return; const dx = e.changedTouches[0].clientX - touchX; if (Math.abs(dx) > 40) stepBox(dx < 0 ? 1 : -1); touchX = null; });
  buildWall();
  setTimeout(queueWall, 1500); setTimeout(queueWall, 4000);
}

/* ---------- the same rooms inside the designer: thumbnails under the preview; tap one, or swipe the preview ---------- */
const VIEWS = ["print", "02", "03", "04", "01"];
const VIEW_CAPTIONS = { "02": "In a bedroom, black frame", "03": "In a living room, oak frame", "04": "In a playroom, white frame", "01": "Four sizes, shown to scale" };
const viewRow = $("pp-views"), roomView = $("pp-roomview");
var viewIndex = 0; // var: queueWall can call paintViews before this line runs
const viewItem = (id) => (id === "01" ? WALL_CHART : WALL_ROOMS.find((r) => r.id === id));
const dpr2 = () => Math.min(2, window.devicePixelRatio || 1);
function paintViewThumbs() {
  // not while the row is hidden (B's page before Customise is tapped): its room photos would load for nothing
  if (!viewRow || !wallArt || !viewRow.getClientRects().length) return;
  viewRow.querySelectorAll(".pp-view").forEach((b) => {
    const id = b.dataset.view, canvas = b.querySelector("canvas");
    if (!canvas || b.__drawn === wallArtVersion) return;
    const w = Math.max(40, Math.round(b.clientWidth * dpr2()));
    if (id === "print") {
      // the plain design, whole, on its paper
      canvas.width = w; canvas.height = w;
      const g = canvas.getContext("2d"); g.fillStyle = "#F6F3EE"; g.fillRect(0, 0, w, w);
      const k = Math.min(w * 0.9 / wallArt.width, w * 0.9 / wallArt.height), aw = wallArt.width * k, ah = wallArt.height * k;
      g.drawImage(wallArt, (w - aw) / 2, (w - ah) / 2, aw, ah);
      b.__drawn = wallArtVersion; b.classList.add("is-live");
      return;
    }
    const version = wallArtVersion;
    wallPhoto(id).then((photo) => {
      try { canvas.width = w; canvas.height = w; renderRoom(canvas, viewItem(id), photo, wallArt); b.__drawn = version; b.classList.add("is-live"); } catch (e) {}
    }, () => {});
  });
}
function paintRoomView() {
  if (!roomView) return;
  const id = VIEWS[viewIndex], wrap = roomView.parentNode;
  if (id === "print") { roomView.hidden = true; wrap.classList.remove("is-room"); return; }
  const item = viewItem(id), img = roomView.querySelector("img"), canvas = roomView.querySelector("canvas");
  const src = (window.PP_ASSETS && PP_ASSETS.wall && PP_ASSETS.wall[id]) || "";
  if (img.getAttribute("src") !== src) img.src = src;
  img.alt = item.chart ? item.caption : "";
  $("pp-roomview-cap").textContent = VIEW_CAPTIONS[id] || "";
  roomView.hidden = false; wrap.classList.add("is-room");
  if (item.chart) { canvas.classList.remove("is-live"); return; }
  canvas.setAttribute("aria-label", "Their design " + (VIEW_CAPTIONS[id] || "").toLowerCase());
  const want = id + ":" + wallArtVersion;
  if (roomView.__drawn === want) return;
  if (roomView.__showing !== id) canvas.classList.remove("is-live"); // never show the last room's picture over this one
  roomView.__showing = id;
  const size = Math.min(1100, Math.max(200, Math.round(roomView.querySelector(".pp-roomview__sq").clientWidth * dpr2())));
  bakeArt(Math.min(1000, Math.max(540, size))).then((art) => wallPhoto(id).then((photo) => {
    if (VIEWS[viewIndex] !== id || !(art || wallArt)) return;
    try { canvas.width = size; canvas.height = size; renderRoom(canvas, item, photo, art || wallArt); canvas.classList.add("is-live"); roomView.__drawn = want; } catch (e) {}
  }), () => {});
}
function paintViews() { paintViewThumbs(); if (VIEWS[viewIndex] !== "print") paintRoomView(); }
function showView(i) {
  viewIndex = (i + VIEWS.length) % VIEWS.length;
  viewRow.querySelectorAll(".pp-view").forEach((b) => b.setAttribute("aria-selected", String(b.dataset.view === VIEWS[viewIndex])));
  paintRoomView();
  try { if (window.posthog && posthog.capture) posthog.capture("pp_room_view", { view: VIEWS[viewIndex], lp_arm: window.LP && LP.arm }); } catch (e) {}
}
if (viewRow && roomView) {
  viewRow.addEventListener("click", (e) => { const b = e.target.closest(".pp-view"); if (b) showView(VIEWS.indexOf(b.dataset.view)); });
  viewRow.addEventListener("keydown", (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault(); showView(viewIndex + (e.key === "ArrowRight" ? 1 : -1));
    viewRow.querySelector('[aria-selected="true"]').focus();
  });
  // a sideways swipe on the preview flicks through the same five; an up or down drag is left to the page
  const wrap = roomView.parentNode;
  let t0 = null;
  wrap.addEventListener("touchstart", (e) => { t0 = e.touches.length === 1 ? [e.touches[0].clientX, e.touches[0].clientY] : null; }, { passive: true });
  wrap.addEventListener("touchend", (e) => {
    if (!t0) return;
    const dx = e.changedTouches[0].clientX - t0[0], dy = e.changedTouches[0].clientY - t0[1]; t0 = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) showView(viewIndex + (dx < 0 ? 1 : -1));
  }, { passive: true });
  paintViews();
  // the row is hidden on B's page until Customise is tapped: draw it when it shows
  try { new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) paintViews(); }).observe(viewRow); } catch (e) {}
}

})();
